import axios from 'axios';

const _rawApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = _rawApiUrl.replace('localhost', window.location.hostname);
export { API_URL };

const LOGIN_REQUEST_TIMEOUT_MS = 30000;

const api = axios.create({
  baseURL: API_URL,
});

// Token refresh state management
let isRefreshing = false;
let refreshSubscribers = [];
let lastRefreshTime = 0;
const REFRESH_INTERVAL = 10 * 60 * 1000; // Proactive refresh every 10 minutes (access token is 15 min)
const TOKEN_EXPIRY_BUFFER_SECONDS = 120; // Refresh if token expires within 2 minutes

// Portal token refresh state (separate from app token)
let isPortalRefreshing = false;
let portalRefreshSubscribers = [];

// Decode JWT payload without dependency
const decodeTokenPayload = (token) => {
  try {
    const base64Payload = token.split('.')[1];
    if (!base64Payload) return null;
    const payload = atob(base64Payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  } catch (e) {
    return null;
  }
};

const isTokenExpiredOrExpiringSoon = (token) => {
  const payload = decodeTokenPayload(token);
  if (!payload || !payload.exp) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp - nowSeconds < TOKEN_EXPIRY_BUFFER_SECONDS;
};

// Function to check if token needs refresh
const shouldRefreshToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return false;
  // Always refresh if token is about to expire, otherwise throttle by interval
  if (isTokenExpiredOrExpiringSoon(token)) return true;
  return (Date.now() - lastRefreshTime) >= REFRESH_INTERVAL;
};

const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

// Function to refresh the token using the refresh token
const performTokenRefresh = async () => {
  if (isRefreshing) {
    // Queue this request until refresh completes
    return new Promise((resolve) => {
      addRefreshSubscriber((newToken) => {
        resolve(newToken);
      });
    });
  }

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  isRefreshing = true;
  try {
    const response = await axios.post(`${API_URL}/api/auth/refresh`, {
      refresh_token: refreshToken
    });

    if (response.data?.access_token) {
      localStorage.setItem('token', response.data.access_token);
      if (response.data?.refresh_token) {
        localStorage.setItem('refreshToken', response.data.refresh_token);
      }
      // Mirror the new access token into the ospito_token cookie so nginx
      // auth_request can continue to gate /uploads/ static files for <img> tags.
      // SameSite=Strict: uploads are same-origin, so Strict is safe here.
      try {
        document.cookie = `ospito_token=${encodeURIComponent(response.data.access_token)}; path=/; SameSite=Strict${window.location.protocol === 'https:' ? '; Secure' : ''}`;
      } catch (e) { /* ignore */ }
      lastRefreshTime = Date.now();
      onTokenRefreshed(response.data.access_token);
      return response.data.access_token;
    }
  } catch (error) {
    // If refresh fails, the refresh token is likely expired: force re-login
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    try {
      document.cookie = 'ospito_token=; path=/; Max-Age=0';
    } catch (e) { /* ignore */ }
    const currentPath = window.location?.pathname || '';
    const isProtectedAppPath = currentPath.startsWith('/app');
    if (isProtectedAppPath && currentPath !== '/login') {
      window.location.href = '/login';
    }
  } finally {
    isRefreshing = false;
  }
  return null;
};

// Function to refresh the portal token using the portal refresh token
const performPortalTokenRefresh = async () => {
  if (isPortalRefreshing) {
    return new Promise((resolve) => {
      portalRefreshSubscribers.push((newToken) => resolve(newToken));
    });
  }

  const refreshToken = localStorage.getItem('portalRefreshToken');
  if (!refreshToken) return null;

  isPortalRefreshing = true;
  try {
    const response = await axios.post(`${API_URL}/api/portal/refresh`, {
      refresh_token: refreshToken
    });

    if (response.data?.access_token) {
      localStorage.setItem('portalToken', response.data.access_token);
      if (response.data?.refresh_token) {
        localStorage.setItem('portalRefreshToken', response.data.refresh_token);
      }
      portalRefreshSubscribers.forEach((cb) => cb(response.data.access_token));
      portalRefreshSubscribers = [];
      return response.data.access_token;
    }
  } catch (error) {
    // Portal refresh failed — clear portal tokens
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalRefreshToken');
    portalRefreshSubscribers = [];
  } finally {
    isPortalRefreshing = false;
  }
  return null;
};

// Request interceptor to add auth token and trigger refresh if needed
api.interceptors.request.use(
  async (config) => {
    const isPortalEndpoint = config.url?.includes('/api/portal/');
    const portalToken = localStorage.getItem('portalToken');

    if (isPortalEndpoint && portalToken) {
      // Portal endpoints always use the portal token, never the app token.
      // If the portal token is expired/expiring soon, try to refresh it
      // before sending the request.
      if (isTokenExpiredOrExpiringSoon(portalToken)) {
        const newToken = await performPortalTokenRefresh();
        if (newToken) {
          config.headers.Authorization = `Bearer ${newToken}`;
        } else {
          config.headers.Authorization = `Bearer ${portalToken}`;
        }
      } else {
        config.headers.Authorization = `Bearer ${portalToken}`;
      }
    } else {
      const token = localStorage.getItem('token');
      if (token) {
        // Check if token needs refresh before sending request
        const isAuthEndpoint = config.url?.includes('/api/auth/');
        if (!isAuthEndpoint && shouldRefreshToken()) {
          const newToken = await performTokenRefresh();
          if (newToken) {
            config.headers.Authorization = `Bearer ${newToken}`;
          } else {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } else {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const reqUrl = originalRequest?.url || '';
    const isPortalRequest = reqUrl.includes('/api/portal/');

    // If 401 and we haven't already retried, attempt token refresh
    if (error.response?.status === 401 && originalRequest && !originalRequest._retried) {
      const isAuthRequest = reqUrl.includes('/api/auth/login') || reqUrl.includes('/api/auth/refresh');
      const isPortalAuthRequest = reqUrl.includes('/api/portal/login') || reqUrl.includes('/api/portal/refresh');

      if (!isAuthRequest && !isPortalRequest) {
        originalRequest._retried = true;
        const newToken = await performTokenRefresh();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      }

      // Portal 401: try refreshing the portal token and retry
      if (isPortalRequest && !isPortalAuthRequest) {
        originalRequest._retried = true;
        const newPortalToken = await performPortalTokenRefresh();
        if (newPortalToken) {
          originalRequest.headers.Authorization = `Bearer ${newPortalToken}`;
          return api(originalRequest);
        }
      }
    }
    
    if (error.response?.status === 401 && !isPortalRequest) {
      const currentPath = window.location?.pathname || '';
      const requestAuthHeader = error.config?.headers?.Authorization || error.config?.headers?.authorization;
      const hasStoredToken = Boolean(localStorage.getItem('token'));
      const hadSession = Boolean(hasStoredToken || requestAuthHeader);

      // Clear stale session artifacts only when the failing request had auth context
      if (hadSession) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      }

      // Avoid infinite reload loops on login pages or during login requests
      const isOnLoginPage = currentPath === '/login';
      const isAuthLoginRequest = reqUrl.includes('/api/auth/login');
      const isProtectedAppPath = currentPath.startsWith('/app');

      const isBloodBankSaveRequest = reqUrl.includes('/api/blood-bank/requests') && !reqUrl.includes('?');
      if (hadSession && isProtectedAppPath && !isOnLoginPage && !isAuthLoginRequest && !isBloodBankSaveRequest) {
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (username, password, twoFactorToken, turnstileToken, deviceId) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    // Send 2FA / Turnstile tokens as form fields (POST body) instead of URL
    // query params so they don't leak into nginx access logs.
    if (twoFactorToken) formData.append('two_factor_token', twoFactorToken);
    if (turnstileToken) formData.append('turnstile_token', turnstileToken);
    if (deviceId) formData.append('device_id', deviceId);
    return api.post('/api/auth/login', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: LOGIN_REQUEST_TIMEOUT_MS,
    });
  },
  googleSignIn: (credential) => api.post('/api/auth/google', { credential }),
  googleVerify2FA: (pendingToken, code) => api.post('/api/auth/google/verify-2fa', { pending_token: pendingToken, code }),
  checkCaptchaRequired: () => api.get('/api/auth/captcha-required'),
  register: (userData) => api.post('/api/auth/register', userData),
  getCurrentUser: () => api.get('/api/auth/me'),
  refreshToken: () => {
    const refreshToken = localStorage.getItem('refreshToken');
    return api.post('/api/auth/refresh', { refresh_token: refreshToken });
  },
  logout: () => {
    const refreshToken = localStorage.getItem('refreshToken');
    return api.post('/api/auth/logout', { refresh_token: refreshToken });
  },
  verifyAdminPin: (pin) => api.post('/api/auth/verify-admin-pin', { pin }),

  // 2FA - Enforced setup (for admin/tenant-owner accounts)
  enforcedSetup2FA: (setupToken) => api.post('/api/2fa/enforced/setup', { setup_token: setupToken }),
  enforcedVerify2FA: (setupToken, token) => api.post('/api/2fa/enforced/verify-setup', { setup_token: setupToken, token }),
  // Demo Email OTP
  sendDemoOtp: (username, passwordVerifiedToken) => api.post('/api/auth/demo-otp/send', { username, password_verified_token: passwordVerifiedToken }),
  verifyDemoOtp: (username, otpCode) => api.post('/api/auth/demo-otp/verify', { username, otp_code: otpCode }),
  // New-Device Email OTP
  sendDeviceOtp: (username, deviceToken) => api.post('/api/auth/device-otp/send', { username, device_token: deviceToken }),
  verifyDeviceOtp: (username, otpCode, deviceId, rememberDevice = true) => api.post('/api/auth/device-otp/verify', { username, otp_code: otpCode, device_id: deviceId, remember_device: rememberDevice }),
  // 2FA - Standard
  setup2FA: () => api.post('/api/2fa/setup'),
  verify2FASetup: (token) => api.post('/api/2fa/verify-setup', { token }),
  disable2FA: (password) => api.post('/api/2fa/disable', { password }),
  get2FAStatus: () => api.get('/api/2fa/status'),

  // Portal Accounts (public customer identity)
  // Registration requires a verified email: send a code, verify it, then
  // register passing that code back (the backend rejects unverified emails).
  portalSendOtp: (email, name) => api.post('/api/portal/otp/send', { email, name }),
  portalVerifyOtp: (email, otpCode) => api.post('/api/portal/otp/verify', { email, otp_code: otpCode }),
  portalRegister: (name, email, password, otpCode) => api.post('/api/portal/register', { name, email, password, otp_code: otpCode }),
  portalLogin: (email, password) => api.post('/api/portal/login', { email, password }),
  portalGoogleSignIn: (credential) => api.post('/api/portal/google', { credential }),
  portalMicrosoftSignIn: (credential) => api.post('/api/portal/microsoft', { credential }),
  portalAppleSignIn: (credential) => api.post('/api/portal/apple', { credential }),
  portalRefresh: (refreshToken) => api.post('/api/portal/refresh', { refresh_token: refreshToken }),
  portalMe: () => api.get('/api/portal/me'),
  portalLogout: (refreshToken) => api.post('/api/portal/logout', refreshToken ? { refresh_token: refreshToken } : undefined),

  // User Management (Admin)
  getAllUsers: (params) => api.get('/api/auth/users', { params }),
  getDoctors: () => api.get('/api/auth/users/doctors'),
  getNurses: () => api.get('/api/auth/users/nurses'),
  getUser: (id) => api.get(`/api/auth/users/${id}`),
  updateUser: (id, data) => api.put(`/api/auth/users/${id}`, data),
  deleteUser: (id, params) => api.delete(`/api/auth/users/${id}`, { params }),
  changePassword: (id, data) => api.post(`/api/auth/users/${id}/change-password`, data),
  changeExpiredPassword: (expiryToken, newPassword) => api.post('/api/auth/change-expired-password', { expiry_token: expiryToken, new_password: newPassword }),
  resetPassword: (id) => api.post(`/api/auth/users/${id}/reset-password`),
  // Self-service forgot / reset password (public, no auth)
  forgotPassword: (identifier) => api.post('/api/auth/forgot-password', { identifier }),
  resetPasswordWithToken: (token, newPassword) => api.post('/api/auth/reset-password', { token, new_password: newPassword }),

  // Permissions Management
  getPermissionModules: () => api.get('/api/auth/permissions/modules'),
  getMyPermissions: () => api.get('/api/auth/me/permissions'),
  getUserPermissions: (userId) => api.get(`/api/auth/users/${userId}/permissions`),
  updateUserPermissions: (userId, data) => api.put(`/api/auth/users/${userId}/permissions`, data),
  clearUserPermissions: (userId) => api.delete(`/api/auth/users/${userId}/permissions`),

  // Session Management (admin)
  getActiveSessions: () => api.get('/api/auth/sessions/active'),
  getSessionHistory: (params) => api.get('/api/auth/sessions/history', { params }),
  revokeSession: (sessionId) => api.post(`/api/auth/sessions/${sessionId}/revoke`),
  revokeAllUserSessions: (userId) => api.post(`/api/auth/sessions/user/${userId}/revoke-all`),

  // Profile Management (self)
  updateMyProfile: (data) => api.put('/api/auth/me', data),
  changeMyPassword: (data) => api.post('/api/auth/me/change-password', data),
  uploadProfilePicture: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/auth/me/profile-picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteProfilePicture: () => api.delete('/api/auth/me/profile-picture'),
};

// Feedback API
// publicStats is fetched by both Landing and Pricing on every visit, and
// React.StrictMode double-invokes effects in dev — so a single page load can
// fire 2-4 requests. Cache the result for 60s to dedupe within and across pages.
let _publicStatsCache = null;
let _publicStatsCacheAt = 0;
const PUBLIC_STATS_TTL_MS = 60_000;

export const feedbackAPI = {
  submit: (data) => api.post('/api/feedback', data),
  myHistory: () => api.get('/api/feedback/my'),
  notifications: () => api.get('/api/feedback/notifications'),
  reply: (feedbackId, reply) => api.post(`/api/feedback/${feedbackId}/reply`, { reply }),
  publicReviews: (limit = 20) => api.get('/api/feedback/public-reviews', { params: { limit } }),
  publicStats: () => {
    const now = Date.now();
    if (_publicStatsCache && (now - _publicStatsCacheAt) < PUBLIC_STATS_TTL_MS) {
      return Promise.resolve(_publicStatsCache);
    }
    return api.get('/api/feedback/public-stats').then((resp) => {
      _publicStatsCache = resp;
      _publicStatsCacheAt = now;
      return resp;
    });
  },
  // Portal feedback history and replies for legacy submissions
  portalMyHistory: () => api.get('/api/portal/feedback/my'),
  portalNotifications: () => api.get('/api/portal/notifications'),
  portalReply: (feedbackId, reply) => api.post(`/api/portal/feedback/${feedbackId}/reply`, { reply }),
};

// Staff Invitation API
export const invitationAPI = {
  // Admin endpoints (require auth)
  invite: (data) => api.post('/api/invitations/invite', data),
  list: () => api.get('/api/invitations/list'),
  resend: (invitationId) => api.post(`/api/invitations/resend/${invitationId}`),
  cancel: (invitationId) => api.delete(`/api/invitations/${invitationId}`),

  // Public endpoints (no auth required)
  verify: (token) => api.get(`/api/invitations/verify/${token}`),
  accept: (token, data) => api.post(`/api/invitations/accept/${token}`, data),
};

// Patient APIs
export const patientAPI = {
  getAll: (params) => api.get('/api/patients', { params }),
  getById: (id) => api.get(`/api/patients/${id}`),
  create: (data) => api.post('/api/patients', data),
  getRegistrationLimit: () => api.get('/api/patients/registration-limit'),
  update: (id, data) => api.put(`/api/patients/${id}`, data),
  delete: (id) => api.delete(`/api/patients/${id}`),
  getAnalytics: (params = {}) => api.get('/api/patients/analytics/comprehensive', { params }),

  // QR Code API
  getQRData: (id, visitId = null) => api.get(`/api/patients/${id}/qr-data`, { params: { visit_id: visitId } }),

  // Photo Upload
  uploadPhoto: (id, formData) => api.post(`/api/patients/${id}/upload-photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  removePhoto: (id) => api.delete(`/api/patients/${id}/photo`),

  // Patient Detail with related data
  getDetail: (id) => api.get(`/api/patients/${id}/detail`),

  // Allergy APIs
  getAllergies: (patientId) => api.get(`/api/patients/${patientId}/allergies`),
  createAllergy: (patientId, data) => api.post(`/api/patients/${patientId}/allergies`, data),
  updateAllergy: (patientId, allergyId, data) => api.put(`/api/patients/${patientId}/allergies/${allergyId}`, data),
  deleteAllergy: (patientId, allergyId) => api.delete(`/api/patients/${patientId}/allergies/${allergyId}`),

  // Chronic Condition APIs
  getChronicConditions: (patientId) => api.get(`/api/patients/${patientId}/chronic-conditions`),
  createChronicCondition: (patientId, data) => api.post(`/api/patients/${patientId}/chronic-conditions`, data),
  updateChronicCondition: (patientId, conditionId, data) => api.put(`/api/patients/${patientId}/chronic-conditions/${conditionId}`, data),
  deleteChronicCondition: (patientId, conditionId) => api.delete(`/api/patients/${patientId}/chronic-conditions/${conditionId}`),

  // Next of Kin APIs
  getNextOfKin: (patientId) => api.get(`/api/patients/${patientId}/next-of-kin`),
  createNextOfKin: (patientId, data) => api.post(`/api/patients/${patientId}/next-of-kin`, data),
  updateNextOfKin: (patientId, nokId, data) => api.put(`/api/patients/${patientId}/next-of-kin/${nokId}`, data),
  deleteNextOfKin: (patientId, nokId) => api.delete(`/api/patients/${patientId}/next-of-kin/${nokId}`),

  // ICD-10 Search
  searchICD10: (query, chronicOnly = false, limit = 20) => api.get('/api/patients/icd10/search', {
    params: { q: query, chronic_only: chronicOnly, limit }
  }),

  // Blood Pressure History (aggregated across all modules)
  getBloodPressureHistory: (patientId) => api.get(`/api/patients/${patientId}/blood-pressure-history`),

  // Blood Sugar History (aggregated across all modules + lab RBS/FBS)
  getBloodSugarHistory: (patientId) => api.get(`/api/patients/${patientId}/blood-sugar-history`),

  // Weight History (aggregated across all modules)
  getWeightHistory: (patientId) => api.get(`/api/patients/${patientId}/weight-history`),

  // Drug-Allergy Checking
  checkDrugAllergy: (patientId, drugName) => api.post(`/api/patients/${patientId}/check-drug-allergy`, null, {
    params: { drug_name: drugName }
  }),

  // Chronic Disease Register
  getChronicDiseaseRegister: (conditionCode = null) => api.get('/api/patients/chronic-disease-register/by-condition', {
    params: { condition_code: conditionCode }
  }),
  getFollowUpDue: (daysAhead = 7, overdueOnly = false) => api.get('/api/patients/chronic-disease-register/follow-up-due', {
    params: { days_ahead: daysAhead, overdue_only: overdueOnly }
  }),

  // Patient Tags APIs
  getTagTypes: () => api.get('/api/patients/tag-types'),
  getTags: (patientId) => api.get(`/api/patients/${patientId}/tags`),
  createTag: (patientId, data) => api.post(`/api/patients/${patientId}/tags`, data),
  deleteTag: (patientId, tagId) => api.delete(`/api/patients/${patientId}/tags/${tagId}`),

  // Patient Documents APIs
  getDocumentTypes: () => api.get('/api/patients/document-types'),
  getDocuments: (patientId, documentType = null) => api.get(`/api/patients/${patientId}/documents`, {
    params: { document_type: documentType }
  }),
  uploadDocument: (patientId, formData) => api.post(`/api/patients/${patientId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteDocument: (patientId, documentId) => api.delete(`/api/patients/${patientId}/documents/${documentId}`),

  // Walk-in service dispatch: patient self-requests tests, no consultation.
  // Creates the lab orders + invoice and completes the check-in record.
  dispatchServiceRequest: (data) => api.post('/api/patients/service-dispatch', data),

  // Patient Family Links APIs
  getRelationshipTypes: () => api.get('/api/patients/relationship-types'),
  getFamilyLinks: (patientId) => api.get(`/api/patients/${patientId}/family`),
  createFamilyLink: (patientId, data) => api.post(`/api/patients/${patientId}/family`, data),
  deleteFamilyLink: (patientId, linkId) => api.delete(`/api/patients/${patientId}/family/${linkId}`),
};

// Patient Portal booking APIs
export const patientBookingAPI = {
  list: (params) => api.get('/api/patient-bookings', { params }),
  matchCandidates: (id) => api.get(`/api/patient-bookings/${id}/match-candidates`),
  decline: (id, data) => api.post(`/api/patient-bookings/${id}/decline`, data),
  schedule: (id, data) => api.post(`/api/patient-bookings/${id}/schedule`, data),
  checkIn: (id) => api.post(`/api/patient-bookings/${id}/check-in`),
  facilities: (params) => api.get('/api/public/patient-portal/facilities', { params }),
  createPublic: (data) => api.post('/api/public/patient-portal/bookings', data),
  verifyPublic: (data) => api.post('/api/public/patient-portal/bookings/verify', data),
  resendPublicCode: (data) => api.post('/api/public/patient-portal/bookings/resend', data),
  requestHistoryCode: (data) => api.post('/api/public/patient-portal/history/request-code', data),
  verifyHistoryCode: (data) => api.post('/api/public/patient-portal/history/verify', data),
  getHistory: (token) => api.get('/api/public/patient-portal/history', { params: { token } }),
  getPublic: (reference, token) => api.get(`/api/public/patient-portal/bookings/${encodeURIComponent(reference)}`, { params: { token } }),
  cancelPublic: (reference, token) => api.post('/api/public/patient-portal/bookings/cancel', { token }, { params: { booking_reference: reference } }),
  reschedulePublic: (reference, data) => api.post('/api/public/patient-portal/bookings/reschedule', data, { params: { booking_reference: reference } }),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/api/dashboard/stats'),
  getPatientStatistics: () => api.get('/api/dashboard/patient-statistics'),
  getRoomOccupancy: () => api.get('/api/dashboard/room-occupancy'),
  getFinancialOverview: () => api.get('/api/dashboard/financial-overview'),
};

// Appointment APIs
export const appointmentAPI = {
  getAll: (params) => api.get('/api/appointments', { params }),
  getById: (id) => api.get(`/api/appointments/${id}`),
  create: (data) => api.post('/api/appointments', data),
  update: (id, data) => api.put(`/api/appointments/${id}`, data),
  delete: (id) => api.delete(`/api/appointments/${id}`),
  getCalendarMonth: (year, month) => api.get('/api/appointments/calendar/month', { params: { year, month } }),
};

// Clinical/EMR APIs
export const clinicalAPI = {
  // Visits
  createVisit: (data) => api.post('/api/clinical/visits', data),
  getVisits: (params) => api.get('/api/clinical/visits', { params }),
  getVisitsByPatient: (patientId) => api.get(`/api/clinical/visits/patient/${patientId}`),

  // Prescriptions
  getPrescribedDrugsForPatient: (patientId) => api.get(`/api/clinical/prescriptions/patient/${patientId}/drugs`),
  getVisit: (id) => api.get(`/api/clinical/visits/${id}`),
  updateVisit: (id, data) => api.put(`/api/clinical/visits/${id}`, data),
  getResultsReady: (params) => api.get('/api/clinical/results-ready', { params }),
  // Acknowledge returned results — stamps reviewed_by/at on the given orders.
  reviewResults: (data) => api.post('/api/clinical/results-ready/review', data),
  getVisitBillingOverrides: (visitId) => api.get(`/api/clinical/visits/${visitId}/billing-overrides`),
  updateVisitBillingOverrides: (visitId, data) => api.patch(`/api/clinical/visits/${visitId}/billing-overrides`, data),

  // Vital Signs
  createVitalSigns: (data) => api.post('/api/clinical/vital-signs', data),
  getVitalSignsByVisit: (visitId) => api.get(`/api/clinical/vital-signs/visit/${visitId}`),
  getVisitVitalsTimeline: (visitId) => api.get(`/api/clinical/visits/${visitId}/vitals-timeline`),

  // Clinical Notes
  createClinicalNote: (data) => api.post('/api/clinical/clinical-notes', data),
  getClinicalNotesByVisit: (visitId) => api.get(`/api/clinical/clinical-notes/visit/${visitId}`),
  updateClinicalNote: (id, data) => api.put(`/api/clinical/clinical-notes/${id}`, data),
  getLastExamChart: (patientId, excludeVisitId = null) => api.get(`/api/clinical/clinical-notes/last-exam-chart/${patientId}`, {
    params: excludeVisitId ? { exclude_visit_id: excludeVisitId } : {},
  }),

  // Diagnoses
  createDiagnosis: (data) => api.post('/api/clinical/diagnoses', data),
  getDiagnosesByVisit: (visitId) => api.get(`/api/clinical/diagnoses/visit/${visitId}`),
  getDiagnosesByPatient: (patientId) => api.get(`/api/clinical/diagnoses/patient/${patientId}`),

  // Prescriptions
  createPrescription: (data) => api.post('/api/clinical/prescriptions', data),
  getPrescriptionsByVisit: (visitId) => api.get(`/api/clinical/prescriptions/visit/${visitId}`),
  getPrescriptionsByPatient: (patientId) => api.get(`/api/clinical/prescriptions/patient/${patientId}`),

  // Lab Orders (from Clinical)
  createLabOrderFromClinical: (data) => api.post('/api/clinical/lab-orders', data),
  createBatchLabOrders: (data) => api.post('/api/clinical/lab-orders/batch', data),
  deleteLabOrder: (id) => api.delete(`/api/clinical/lab-orders/${id}`),

  // External Orders
  createExternalLabOrder: (data) => api.post('/api/clinical/external-orders/lab', data),
  createExternalImagingOrder: (data) => api.post('/api/clinical/external-orders/imaging', data),
  createExternalProcedureOrder: (data) => api.post('/api/clinical/external-orders/procedure', data),
  getExternalOrdersByPatient: (patientId) => api.get(`/api/clinical/external-orders/patient/${patientId}`),

  // Patient History
  getPatientHistory: (patientId) => api.get(`/api/clinical/patient-history/${patientId}`),

  // === MISSING METHODS ADDED ===

  // Diagnosis Management
  updateDiagnosis: (id, data) => api.put(`/api/clinical/diagnoses/${id}`, data),
  deleteDiagnosis: (id) => api.delete(`/api/clinical/diagnoses/${id}`),
  updateDiagnosisEvidence: (id, data) => api.put(`/api/clinical/diagnoses/${id}/evidence`, data),

  // Prescription Management
  deletePrescription: (id) => api.delete(`/api/clinical/prescriptions/${id}`),
  deletePrescriptionItem: (itemId) => api.delete(`/api/clinical/prescription-items/${itemId}`),
  updatePrescriptionItem: (itemId, data) => api.patch(`/api/clinical/prescription-items/${itemId}`, data),
  updatePrescription: (id, data) => api.patch(`/api/clinical/prescriptions/${id}`, data),

  // Visit Completeness & Closure
  checkVisitCompleteness: (visitId) => api.get(`/api/clinical/visits/${visitId}/completeness`),
  getVisitSummary: (visitId) => api.get(`/api/clinical/visits/${visitId}/summary`),
  getVisitTimeline: (visitId) => api.get(`/api/clinical/visits/${visitId}/timeline`),
  closeVisit: (visitId, data) => api.post(`/api/clinical/visits/${visitId}/close`, data),

  // Alias methods for consistent naming (used by ClinicalWorkspace)
  getVisitVitals: (visitId) => api.get(`/api/clinical/vital-signs/visit/${visitId}`),
  getVisitNotes: (visitId) => api.get(`/api/clinical/clinical-notes/visit/${visitId}`),
  getVisitDiagnoses: (visitId) => api.get(`/api/clinical/diagnoses/visit/${visitId}`),
  getVisitPrescriptions: (visitId) => api.get(`/api/clinical/prescriptions/visit/${visitId}`),
  getVisitLabOrders: (visitId) => api.get(`/api/clinical/lab-orders/visit/${visitId}`),

  // Patient Vitals History
  getPatientVitals: (patientId) => api.get(`/api/clinical/vital-signs/patient/${patientId}`),

  // Enhanced Vital Signs (vital_signs_enhanced router)
  getPatientVitalsTrends: (patientId, days = 30) => api.get(`/api/vital-signs/patient/${patientId}/trends`, { params: { days } }),
  getPatientLatestVitals: (patientId) => api.get(`/api/vital-signs/patient/${patientId}/latest`),
  getPatientVitalsAlerts: (patientId) => api.get(`/api/vital-signs/patient/${patientId}/alerts`),
  getAbnormalVitalsAlerts: (hours = 24, severity) => api.get('/api/vital-signs/abnormal-alerts', { params: { hours, severity } }),
  getPatientGrowthChart: (patientId) => api.get(`/api/vital-signs/patient/${patientId}/growth-chart`),
  comparePatientVitals: (patientId) => api.get(`/api/vital-signs/patient/${patientId}/compare`),

  // Care Plans
  getCarePlans: (patientId) => api.get(`/api/clinical/care-plans/patient/${patientId}`),
  getCarePlan: (id) => api.get(`/api/clinical/care-plans/${id}`),
  createCarePlan: (data) => api.post('/api/clinical/care-plans', data),
  updateCarePlan: (id, data) => api.put(`/api/clinical/care-plans/${id}`, data),
  deleteCarePlan: (id) => api.delete(`/api/clinical/care-plans/${id}`),

  // Care Plan Goals
  addCarePlanGoal: (planId, data) => api.post(`/api/clinical/care-plans/${planId}/goals`, data),
  updateCarePlanGoal: (planId, goalId, data) => api.put(`/api/clinical/care-plans/${planId}/goals/${goalId}`, data),
  deleteCarePlanGoal: (planId, goalId) => api.delete(`/api/clinical/care-plans/${planId}/goals/${goalId}`),

  // Visit Disposition (Clinical Integration)
  createDisposition: (visitId, data) => api.post(`/api/clinical/visits/${visitId}/disposition`, data),
  getDisposition: (visitId) => api.get(`/api/clinical/visits/${visitId}/disposition`),

  // Unified Patient Timeline (Phase 1D)
  getPatientTimeline: (patientId, limit = 30) => api.get(`/api/clinical/patient/${patientId}/timeline`, { params: { limit } }),
};

// Clinical Decision Support System (CDSS) APIs
export const cdssAPI = {
  // Consultation
  listComplaints: (params) => api.get('/api/cdss/complaints', { params }),
  getComplaint: (complaintId) => api.get(`/api/cdss/complaints/${complaintId}`),
  getComplaintBySlug: (slug) => api.get(`/api/cdss/complaints/by-slug/${slug}`),
  score: (data) => api.post('/api/cdss/score', data),

  // Sessions
  createSession: (data) => api.post('/api/cdss/sessions', data),
  listSessionsForVisit: (visitId) => api.get(`/api/cdss/sessions/visit/${visitId}`),
  updateSession: (id, data) => api.put(`/api/cdss/sessions/${id}`, data),

  // Admin: Complaints
  createComplaint: (data) => api.post('/api/cdss/complaints', data),
  updateComplaint: (id, data) => api.put(`/api/cdss/complaints/${id}`, data),
  deleteComplaint: (id) => api.delete(`/api/cdss/complaints/${id}`),
  publishComplaint: (id) => api.post(`/api/cdss/complaints/${id}/publish`),

  // Admin: Sections / Questions / Options / Conditions / Investigations
  createSection: (data) => api.post('/api/cdss/sections', data),
  updateSection: (id, data) => api.put(`/api/cdss/sections/${id}`, data),
  deleteSection: (id) => api.delete(`/api/cdss/sections/${id}`),

  createQuestion: (data) => api.post('/api/cdss/questions', data),
  updateQuestion: (id, data) => api.put(`/api/cdss/questions/${id}`, data),
  deleteQuestion: (id) => api.delete(`/api/cdss/questions/${id}`),

  createOption: (questionId, data) => api.post(`/api/cdss/questions/${questionId}/options`, data),
  updateOption: (id, data) => api.put(`/api/cdss/options/${id}`, data),
  deleteOption: (id) => api.delete(`/api/cdss/options/${id}`),

  createCondition: (data) => api.post('/api/cdss/conditions', data),
  updateCondition: (id, data) => api.put(`/api/cdss/conditions/${id}`, data),
  deleteCondition: (id) => api.delete(`/api/cdss/conditions/${id}`),

  createInvestigation: (data) => api.post('/api/cdss/investigations', data),
  updateInvestigation: (id, data) => api.put(`/api/cdss/investigations/${id}`, data),
  deleteInvestigation: (id) => api.delete(`/api/cdss/investigations/${id}`),

  // Import / Export
  import: (data) => api.post('/api/cdss/import', data),
  export: (params) => api.get('/api/cdss/export', { params }),
};

// Pharmacy APIs
export const pharmacyAPI = {
  // Drugs
  getDrugs: (params) => api.get('/api/pharmacy/drugs', { params }),
  getDrugCatalog: (search = '', limit = 200) => api.get('/api/pharmacy/drugs/catalog', { params: { search, limit } }),  // Fast catalog for dropdowns
  getDrug: (id) => api.get(`/api/pharmacy/drugs/${id}`),
  createDrug: (data) => api.post('/api/pharmacy/drugs', data),
  updateDrug: (id, data) => api.put(`/api/pharmacy/drugs/${id}`, data),
  deleteDrug: (id) => api.delete(`/api/pharmacy/drugs/${id}`),

  // Stock
  addStockBatch: (data) => api.post('/api/pharmacy/stock-batches', data),
  createDrugBatch: (data) => api.post('/api/pharmacy/stock-batches', data),
  getDrugStockBatches: (drugId, params) => api.get(`/api/pharmacy/stock-batches/drug/${drugId}`, { params }),
  getExpiringSoon: (days) => api.get('/api/pharmacy/stock-batches/expiring-soon', { params: { days } }),
  adjustDrugStock: (data) => api.post('/api/pharmacy/stock-batches/adjust', data),

  // Prescriptions & Dispensing
  getPendingPrescriptions: (params) => api.get('/api/pharmacy/prescriptions/pending', { params }),
  getPrescriptionHistory: (params) => api.get('/api/pharmacy/prescriptions/history', { params }),
  getPrescription: (id) => api.get(`/api/pharmacy/prescriptions/${id}`),
  dispensePrescription: (id, data) => api.post(`/api/pharmacy/dispense/${id}`, data),
  getDispenseHistory: (params) => api.get('/api/pharmacy/dispense-history', { params }),

  // Invoice Items to Dispense
  getPendingInvoiceItems: (params) => api.get('/api/pharmacy/pending-invoice-items', { params }),
  dispenseInvoiceItems: (invoiceId, data) => api.post(`/api/pharmacy/dispense-invoice/${invoiceId}`, data),
  getDispensingHistory: (invoiceId) => api.get(`/api/pharmacy/dispensing-history/${invoiceId}`),
  getAllDispensingHistory: (params) => api.get('/api/pharmacy/dispensing-history', { params }),

  // Stats
  getStats: () => api.get('/api/pharmacy/stats/overview'),
  getAnalyticsStats: () => api.get('/api/pharmacy/stats/analytics'),
  getAnalyticsDashboard: (days = 30) => api.get('/api/pharmacy/analytics/dashboard', { params: { days } }),

  // Patient Medications (for clinical module)
  getPatientMedications: (patientId, excludeVisitId) => api.get(`/api/pharmacy/medications/patient/${patientId}`, { params: excludeVisitId ? { exclude_visit_id: excludeVisitId } : {} }),
};

// Laboratory APIs
export const laboratoryAPI = {
  // Tests
  getTests: (params) => api.get('/api/laboratory/tests', { params }),
  getTest: (id) => api.get(`/api/laboratory/tests/${id}`),
  createTest: (data) => api.post('/api/laboratory/tests', data),
  updateTest: (id, data) => api.put(`/api/laboratory/tests/${id}`, data),
  deleteTest: (id) => api.delete(`/api/laboratory/tests/${id}`),

  // Parameters
  createParameter: (data) => api.post('/api/laboratory/parameters', data),
  getTestParameters: (testId) => api.get(`/api/laboratory/parameters/test/${testId}`),
  updateParameter: (id, data) => api.put(`/api/laboratory/parameters/${id}`, data),
  deleteParameter: (id) => api.delete(`/api/laboratory/parameters/${id}`),

  // Orders
  getOrders: (params) => api.get('/api/laboratory/orders', { params }),
  getOrder: (id) => api.get(`/api/laboratory/orders/${id}`),
  createOrder: (data) => api.post('/api/laboratory/orders', data),
  updateOrder: (id, data) => api.put(`/api/laboratory/orders/${id}`, data),
  deleteOrder: (id) => api.delete(`/api/laboratory/orders/${id}`),
  collectSample: (orderId, data) => api.post(`/api/laboratory/orders/${orderId}/collect-sample`, data),
  updateOrderStatus: (orderId, status) => api.put(`/api/laboratory/orders/${orderId}`, { status }),

  // Samples
  getSamples: (params) => api.get('/api/laboratory/samples', { params }),
  batchCollectSamples: (data) => api.post('/api/laboratory/samples/batch-collect', data),
  updateSample: (id, data) => api.put(`/api/laboratory/samples/${id}`, data),
  acceptSample: (id, data) => api.post(`/api/laboratory/samples/${id}/accept`, data),
  rejectSample: (id, data) => api.post(`/api/laboratory/samples/${id}/reject`, data),

  // Results
  createResult: (data) => api.post('/api/laboratory/results', data),
  createResultsBatch: (orderId, results) => api.post('/api/laboratory/results/batch', results, { params: { order_id: orderId } }),
  getResultsByOrder: (orderId) => api.get(`/api/laboratory/results/order/${orderId}`),
  getResultsByPatient: (patientId) => api.get(`/api/laboratory/results/patient/${patientId}`),
  updateResult: (id, data) => api.put(`/api/laboratory/results/${id}`, data),
  getTestsByPatient: (patientId) => api.get(`/api/laboratory/tests/patient/${patientId}`),

  // Result rejection / correction workflow (verifier sends results back)
  rejectResults: (data) => api.post('/api/laboratory/orders/reject-results', data),
  resubmitResults: (orderId, data) => api.post(`/api/laboratory/orders/${orderId}/resubmit-results`, data),

  // Extra lab kit deduction (non-billable, for faulty/retry/waste kits)
  deductExtraKits: (data) => api.post('/api/laboratory/kits/deduct-extra', data),

  // External result upload/download
  uploadExternalResult: (orderId, formData) => api.post(`/api/laboratory/orders/${orderId}/upload-external-result`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteExternalResult: (orderId) => api.delete(`/api/laboratory/orders/${orderId}/external-result`),
  downloadExternalResult: (orderId) => api.get(`/api/laboratory/orders/${orderId}/download-external-result`, {
    responseType: 'blob',
  }),

  // Panels
  getPanels: (params) => api.get('/api/laboratory/panels', { params }),
  getPanel: (id) => api.get(`/api/laboratory/panels/${id}`),
  createPanel: (data) => api.post('/api/laboratory/panels', data),
  updatePanel: (id, data) => api.put(`/api/laboratory/panels/${id}`, data),
  deletePanel: (id) => api.delete(`/api/laboratory/panels/${id}`),

  // Stats
  getStats: () => api.get('/api/laboratory/stats/overview'),
  getAnalyticsStats: () => api.get('/api/laboratory/stats/analytics'),
  getDashboardStats: (params) => api.get('/api/laboratory/stats/dashboard', { params }),

  // Quality Control
  getQcControlLots: (params) => api.get('/api/laboratory/qc/control-lots', { params }),
  createQcControlLot: (data) => api.post('/api/laboratory/qc/control-lots', data),
  deleteQcControlLot: (id) => api.delete(`/api/laboratory/qc/control-lots/${id}`),
  getQcRuns: (params) => api.get('/api/laboratory/qc/runs', { params }),
  createQcRun: (data) => api.post('/api/laboratory/qc/runs', data),

  // Consumables (lab consumption log + stock levels)
  getConsumables: (params) => api.get('/api/laboratory/consumables', { params }),
  getConsumablesStockLevels: () => api.get('/api/laboratory/consumables/stock-levels'),
};

// Billing APIs
export const billingAPI = {
  // Invoices
  ensurePosWalkInCustomer: () => api.post('/api/billing/pos/walk-in-customer'),
  searchPosWalkInCustomers: (query) => api.get('/api/billing/pos/walk-in-customers/search', { params: { query } }),
  findOrCreateNamedWalkInCustomer: (data) => api.post('/api/billing/pos/walk-in-customer/named', data),
  posCheckout: (data) => api.post('/api/billing/pos/checkout', data),
  posAutoDispense: (invoiceId) => api.post(`/api/billing/pos/auto-dispense/${invoiceId}`),

  // Live POS drafts (multi-user in-progress sales)
  getPosDrafts: () => api.get('/api/billing/pos/drafts'),
  createPosDraft: (data) => api.post('/api/billing/pos/drafts', data),
  updatePosDraft: (id, data) => api.put(`/api/billing/pos/drafts/${id}`, data),
  claimPosDraft: (id) => api.post(`/api/billing/pos/drafts/${id}/claim`),
  sharePosDraft: (id) => api.post(`/api/billing/pos/drafts/${id}/share`),
  deletePosDraft: (id) => api.delete(`/api/billing/pos/drafts/${id}`),
  getPosCompleted: (params) => api.get('/api/billing/pos/completed', { params }),

  // Partial orders (walk-in installment fulfillment)
  getPosPartialOrders: (params) => api.get('/api/billing/pos/partial-orders', { params }),
  getPosPartialOrder: (id) => api.get(`/api/billing/pos/partial-orders/${id}`),
  closePosPartialOrder: (id) => api.post(`/api/billing/pos/partial-orders/${id}/close`),

  getInvoices: (params) => api.get('/api/billing/invoices', { params }),
  getInvoice: (id) => api.get(`/api/billing/invoices/${id}`),
  createInvoice: (data) => api.post('/api/billing/invoices', data),
  updateInvoice: (id, data) => api.put(`/api/billing/invoices/${id}`, data),
  reorderInvoices: (data) => api.put('/api/billing/invoices/reorder', data),
  deleteInvoice: (id) => api.delete(`/api/billing/invoices/${id}`),
  addInvoiceItems: (invoiceId, data) => api.post(`/api/billing/invoices/${invoiceId}/items`, data),
  updateInvoiceItems: (invoiceId, data) => api.put(`/api/billing/invoices/${invoiceId}/items`, data),
  duplicateInvoice: (invoiceId) => api.post(`/api/billing/invoices/${invoiceId}/duplicate`),
  syncIpdExtras: (invoiceId) => api.post(`/api/billing/invoices/${invoiceId}/sync-ipd-extras`),
  revertInvoiceToQuote: (invoiceId) => api.post(`/api/billing/invoices/${invoiceId}/revert-to-quote`),

  // Payments
  getPayments: (params) => api.get('/api/billing/payments', { params }),
  createPayment: (data) => api.post('/api/billing/payments', data),
  getInvoiceBalance: (invoiceId) => api.get(`/api/billing/invoices/${invoiceId}/balance`),
  getPaymentReceipt: (paymentId) => api.get(`/api/billing/receipts/${paymentId}`),

  // Stats
  getStats: () => api.get('/api/billing/stats/overview'),
  getAnalyticsStats: (days = 90) => api.get('/api/billing/stats/analytics', { params: { days } }),
  getPosTodayStats: () => api.get('/api/billing/stats/pos-today'),

  // Service Catalog
  getServices: (params) => api.get('/api/billing/services', { params }),
  createService: (data) => api.post('/api/billing/services', data),
  updateService: (id, data) => api.put(`/api/billing/services/${id}`, data),
  deleteService: (id) => api.delete(`/api/billing/services/${id}`),

  // Unified Pricing Catalog
  getPricingCatalog: (params) => api.get('/api/billing/pricing-catalog', { params }),
  updateItemPrice: (module, itemId, price) => api.put(`/api/billing/pricing-catalog/${module}/${itemId}`, null, { params: { price } }),

  // Auto-Billing
  createVisitBills: (visitId) => api.post(`/api/billing/visit-bills/${visitId}`),

  // Patient Billed Supplies (stock items from invoices)
  getPatientBilledSupplies: (patientId, params) => api.get(`/api/billing/patient/${patientId}/billed-supplies`, { params }),

  // Reconcile: backfill quantity_dispensed on supply invoice lines from MAR
  // consumables_used history, and auto-bill extras to the patient's daily invoice.
  reconcilePatientSupplyDispensing: (patientId, params) => api.post(`/api/billing/patient/${patientId}/reconcile-supply-dispensing`, null, { params }),

  // Record late supply usage — deducts from billed supplies, auto-bills extras
  recordSupplyUsage: (patientId, data) => api.post(`/api/billing/patient/${patientId}/record-supply-usage`, data),

  // Pharmacy dispatch — cashier sends drug/sundry items to the pharmacy
  // dispensing queue. Recall pulls them back (only before dispensing starts).
  dispatchInvoiceToPharmacy: (invoiceId) => api.post(`/api/billing/invoices/${invoiceId}/dispatch-to-pharmacy`),
  recallInvoiceFromPharmacy: (invoiceId) => api.post(`/api/billing/invoices/${invoiceId}/recall-from-pharmacy`),

  // Expense Categories
  getExpenseCategories: (params) => api.get('/api/billing/expense-categories', { params }),
  createExpenseCategory: (data) => api.post('/api/billing/expense-categories', data),
  updateExpenseCategory: (id, data) => api.put(`/api/billing/expense-categories/${id}`, data),
  deleteExpenseCategory: (id) => api.delete(`/api/billing/expense-categories/${id}`),

  // Expenses
  getExpenses: (params) => api.get('/api/billing/expenses', { params }),
  createExpense: (data) => api.post('/api/billing/expenses', data),
  updateExpense: (id, data) => api.put(`/api/billing/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/api/billing/expenses/${id}`),

  // Credit Notes
  getCreditNotes: (params) => api.get('/api/billing/credit-notes', { params }),

  // Patient Deposits
  getDeposits: (params) => api.get('/api/billing/deposits', { params }),

  // Payment Plans
  getPaymentPlans: (params) => api.get('/api/billing/payment-plans', { params }),
  createPaymentPlan: (data) => api.post('/api/billing/payment-plans', null, { params: data }),
  payInstallment: (planId, data) => api.post(`/api/billing/payment-plans/${planId}/pay-installment`, null, { params: data }),
};


// Returns & Refunds APIs
export const returnsAPI = {
  // Return cases
  getReturns: (params) => api.get('/api/returns', { params }),
  getReturn: (id) => api.get(`/api/returns/${id}`),
  getReturnCounts: () => api.get('/api/returns/counts'),
  createReturn: (data) => api.post('/api/returns', data),
  reviewReturn: (id, data) => api.post(`/api/returns/${id}/review`, data),
  approveReturn: (id, data) => api.post(`/api/returns/${id}/approve`, data),
  rejectReturn: (id, data) => api.post(`/api/returns/${id}/reject`, data),
  getReturnableItems: (invoiceId) => api.get(`/api/returns/returnable/${invoiceId}`),

  // Credit notes
  refundCreditNote: (id, data) => api.post(`/api/billing/credit-notes/${id}/refund`, data),
  applyCreditNote: (id, data) => api.post(`/api/billing/credit-notes/${id}/apply`, data),
};

// Inventory APIs
export const inventoryAPI = {
  // Suppliers
  getSuppliers: (params) => api.get('/api/inventory/suppliers', { params }),
  getSupplier: (id) => api.get(`/api/inventory/suppliers/${id}`),
  createSupplier: (data) => api.post('/api/inventory/suppliers', data),
  updateSupplier: (id, data) => api.put(`/api/inventory/suppliers/${id}`, data),
  deleteSupplier: (id) => api.delete(`/api/inventory/suppliers/${id}`),

  // Warehouses
  getWarehouses: () => api.get('/api/inventory/warehouses'),
  getWarehouse: (id) => api.get(`/api/inventory/warehouses/${id}`),
  createWarehouse: (data) => api.post('/api/inventory/warehouses', data),
  updateWarehouse: (id, data) => api.put(`/api/inventory/warehouses/${id}`, data),
  deleteWarehouse: (id) => api.delete(`/api/inventory/warehouses/${id}`),

  // Stock Items & Services
  getStockItems: (params) => api.get('/api/inventory/items', { params }),
  getStockItem: (id) => api.get(`/api/inventory/items/${id}`),
  createStockItem: (data) => api.post('/api/inventory/items', data),
  updateStockItem: (id, data) => api.put(`/api/inventory/items/${id}`, data),
  patchStockItem: (id, data) => api.patch(`/api/inventory/items/${id}`, data),
  deleteStockItem: (id) => api.delete(`/api/inventory/items/${id}`),

  // Stock Batches
  getStockBatches: (params) => api.get('/api/inventory/batches', { params }),
  createStockBatch: (data) => api.post('/api/inventory/batches', data),

  // Stock Adjustments
  getStockAdjustments: (params) => api.get('/api/inventory/adjustments', { params }),
  createStockAdjustment: (data) => api.post('/api/inventory/adjustments', data),

  // Summary & Stats
  getStockSummary: (params) => api.get('/api/inventory/summary', { params }),
  getStats: () => api.get('/api/inventory/stats'),
  getAnalyticsStats: (days = 90) => api.get('/api/inventory/stats/analytics', { params: { days } }),

  // All Items (Unified)
  getAllItems: (params) => api.get('/api/inventory/all-items', { params }),

  // Lightweight stock-count-due count for sidebar badge
  getStockCountDue: () => api.get('/api/inventory/stock-count-due'),

  // Bulk Adjustments
  createBulkAdjustments: (data) => api.post('/api/inventory/bulk-adjustments', data),

  // Reports
  getStockValuationReport: (params) => api.get('/api/inventory/reports/stock-valuation', { params }),
  getStockMovementReport: (params) => api.get('/api/inventory/reports/stock-movement', { params }),
  getConsumptionAnalysis: (params) => api.get('/api/inventory/reports/consumption-analysis', { params }),

  // Receive Goods (single & bulk) + History
  receiveGoods: (data) => api.post('/api/inventory/receive-goods', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  receiveGoodsBulk: (data) => api.post('/api/inventory/receive-goods/bulk', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getReceivedGoods: (params) => api.get('/api/inventory/received-goods', { params }),
  deleteReceivedGoods: (itemId) => api.delete(`/api/inventory/received-goods/${itemId}`),

  // Write-off expired items
  writeOffExpiredItems: () => api.post('/api/inventory/write-off-expired'),
};

// IPD/Inpatient APIs
export const ipdAPI = {
  // Wards
  getWards: () => api.get('/api/ipd/wards'),
  getWard: (id) => api.get(`/api/ipd/wards/${id}`),
  createWard: (data) => api.post('/api/ipd/wards', data),
  updateWard: (id, data) => api.put(`/api/ipd/wards/${id}`, data),

  // Beds
  getBeds: (params) => api.get('/api/ipd/beds', { params }),
  getBed: (id) => api.get(`/api/ipd/beds/${id}`),
  createBed: (data) => api.post('/api/ipd/beds', data),
  updateBed: (id, data) => api.put(`/api/ipd/beds/${id}`, data),
  updateBedStatus: (id, status) => api.put(`/api/ipd/beds/${id}/status`, null, { params: { status } }),

  // Admissions
  getAdmissions: (params) => api.get('/api/ipd/admissions', { params }),
  getAdmission: (id) => api.get(`/api/ipd/admissions/${id}`),
  createAdmission: (data) => api.post('/api/ipd/admissions', data),
  updateAdmission: (id, data) => api.put(`/api/ipd/admissions/${id}`, data),
  deleteAdmission: (id) => api.delete(`/api/ipd/admissions/${id}`),
  dischargePatient: (id, data) => api.post(`/api/ipd/admissions/${id}/discharge`, data),
  getAdmissionsByPatient: (patientId) => api.get('/api/ipd/admissions', { params: { patient_id: patientId } }),
  getCurrentAdmissions: () => api.get('/api/ipd/admissions', { params: { status: 'ADMITTED' } }),

  // Bed Transfers
  getBedTransfers: (params) => api.get('/api/ipd/transfers', { params }),
  createBedTransfer: (data) => api.post('/api/ipd/transfers', data),

  // Vital Signs
  getVitalSigns: (admissionId) => api.get('/api/ipd/vital-signs', { params: { admission_id: admissionId } }),
  createVitalSigns: (data) => api.post('/api/ipd/vital-signs', data),

  // Medications
  getMedications: (admissionId, status) => api.get('/api/ipd/medications', { params: { admission_id: admissionId, status } }),
  createMedication: (data) => api.post('/api/ipd/medications', data),
  updateMedication: (id, data) => api.put(`/api/ipd/medications/${id}`, data),
  getDueMedications: () => api.get('/api/ipd/medications/due'),
  administerMedication: (medicationId, data) => api.post(`/api/ipd/medications/${medicationId}/administer`, data),
  getSupplyTracking: (admissionId) => api.get(`/api/ipd/admissions/${admissionId}/supply-tracking`),

  // Progress Notes
  getProgressNotes: (admissionId) => api.get('/api/ipd/progress-notes', { params: { admission_id: admissionId } }),
  createProgressNote: (data) => api.post('/api/ipd/progress-notes', data),

  // Stats
  getStats: () => api.get('/api/ipd/stats'),
  getDetailedStats: () => api.get('/api/ipd/stats/detailed'),
  getTrends: (days = 30) => api.get('/api/ipd/stats/trends', { params: { days } }),

  // Prognosis
  createPrognosis: (data) => api.post('/api/ipd/prognosis', data),
  getPrognosisByAdmission: (admissionId, type = null) => api.get(`/api/ipd/prognosis/admission/${admissionId}`, { params: { prognosis_type: type } }),
  getPrognosisByPatient: (patientId) => api.get(`/api/ipd/prognosis/patient/${patientId}`),
  getPrognosis: (id) => api.get(`/api/ipd/prognosis/${id}`),
  updatePrognosis: (id, data) => api.put(`/api/ipd/prognosis/${id}`, data),
  deletePrognosis: (id) => api.delete(`/api/ipd/prognosis/${id}`),

  // Fluid Balance
  getFluidBalance: (admissionId, date = null) => api.get('/api/ipd/fluid-balance', { params: { admission_id: admissionId, date } }),
  getFluidBalanceSummary: (admissionId, date = null) => api.get(`/api/ipd/fluid-balance/summary/${admissionId}`, { params: { date } }),
  createFluidBalance: (data) => api.post('/api/ipd/fluid-balance', data),
  deleteFluidBalance: (id) => api.delete(`/api/ipd/fluid-balance/${id}`),
  getFluidTypes: () => api.get('/api/ipd/fluid-types'),

  // Analytics
  getAnalytics: (days = 30) => api.get('/api/ipd/analytics', { params: { days } }),

  // Nursing Handovers
  getHandovers: (params) => api.get('/api/ipd/handovers', { params }),
  getHandover: (id) => api.get(`/api/ipd/handovers/${id}`),
  createHandover: (data) => api.post('/api/ipd/handovers', data),
  acknowledgeHandover: (id, data) => api.put(`/api/ipd/handovers/${id}/acknowledge`, data),
  deleteHandover: (id) => api.delete(`/api/ipd/handovers/${id}`),

  // Patient Referrals
  getReferrals: (params) => api.get('/api/ipd/referrals', { params }),
  getReferral: (id) => api.get(`/api/ipd/referrals/${id}`),
  createReferral: (data) => api.post('/api/ipd/referrals', data),
  updateReferral: (id, data) => api.put(`/api/ipd/referrals/${id}`, data),

  // Admission Requests
  getAdmissionRequests: (params) => api.get('/api/ipd/admission-requests', { params }),
  getAdmissionRequest: (id) => api.get(`/api/ipd/admission-requests/${id}`),
  createAdmissionRequest: (data) => api.post('/api/ipd/admission-requests', data),
  approveAdmissionRequest: (id, data) => api.put(`/api/ipd/admission-requests/${id}/approve`, data),
  rejectAdmissionRequest: (id, data) => api.put(`/api/ipd/admission-requests/${id}/reject`, data),
  deleteAdmissionRequest: (id) => api.delete(`/api/ipd/admission-requests/${id}`),

  // Clinical Integration (Phases 3B, 3C)
  generateMAR: (admissionId) => api.post(`/api/ipd/admissions/${admissionId}/generate-mar`),
  getSourceVisit: (admissionId) => api.get(`/api/ipd/admissions/${admissionId}/source-visit`),

  // Running Tab / Working Invoice
  getWorkingInvoice: (admissionId) => api.get(`/api/ipd/admissions/${admissionId}/working-invoice`),
  createWorkingInvoice: (admissionId) => api.post(`/api/ipd/admissions/${admissionId}/create-working-invoice`),
  finalizeAdmissionBill: (admissionId) => api.post(`/api/ipd/admissions/${admissionId}/finalize-bill`),

  // Drug Consumable Templates
  getConsumableTemplates: (params) => api.get('/api/ipd/consumable-templates', { params }),
  createConsumableTemplate: (data) => api.post('/api/ipd/consumable-templates', data),
  updateConsumableTemplate: (id, data) => api.put(`/api/ipd/consumable-templates/${id}`, data),
  deleteConsumableTemplate: (id) => api.delete(`/api/ipd/consumable-templates/${id}`),
  suggestConsumables: (params) => api.get('/api/ipd/consumable-templates/suggest', { params }),
};

// OBS/RH APIs
export const maternalAPI = {
  // Pregnancies
  getPregnancies: (params) => api.get('/api/maternal/pregnancies', { params }),
  getPregnancy: (id) => api.get(`/api/maternal/pregnancies/${id}`),
  createPregnancy: (data) => api.post('/api/maternal/pregnancies', data),
  updatePregnancy: (id, data) => api.put(`/api/maternal/pregnancies/${id}`, data),
  deletePregnancy: (id) => api.delete(`/api/maternal/pregnancies/${id}`),

  // ANC Visits
  getANCVisits: (params) => api.get('/api/maternal/anc-visits', { params }),
  getANCVisit: (id) => api.get(`/api/maternal/anc-visits/${id}`),
  createANCVisit: (data) => api.post('/api/maternal/anc-visits', data),
  updateANCVisit: (id, data) => api.put(`/api/maternal/anc-visits/${id}`, data),
  deleteANCVisit: (id) => api.delete(`/api/maternal/anc-visits/${id}`),
  startANCVisit: (data) => api.post('/api/maternal/anc-visits/start', data),
  closeANCVisit: (id) => api.put(`/api/maternal/anc-visits/${id}/close`),
  getOpenANCVisits: () => api.get('/api/maternal/anc-visits/open'),

  // Labour & Delivery
  getLabourRecords: (params) => api.get('/api/maternal/labour-records', { params }),
  getLabourRecord: (id) => api.get(`/api/maternal/labour-records/${id}`),
  createLabourRecord: (data) => api.post('/api/maternal/labour-records', data),
  updateLabourRecord: (id, data) => api.put(`/api/maternal/labour-records/${id}`, data),
  getDeliveries: (params) => api.get('/api/maternal/deliveries', { params }),
  getDelivery: (id) => api.get(`/api/maternal/deliveries/${id}`),
  createDelivery: (data) => api.post('/api/maternal/deliveries', data),
  updateDelivery: (id, data) => api.put(`/api/maternal/deliveries/${id}`, data),
  deleteDelivery: (id) => api.delete(`/api/maternal/deliveries/${id}`),

  // Postnatal
  getPostnatalVisits: (params) => api.get('/api/maternal/postnatal-visits', { params }),
  getPostnatalVisit: (id) => api.get(`/api/maternal/postnatal-visits/${id}`),
  createPostnatalVisit: (data) => api.post('/api/maternal/postnatal-visits', data),
  updatePostnatalVisit: (id, data) => api.put(`/api/maternal/postnatal-visits/${id}`, data),
  deletePostnatalVisit: (id) => api.delete(`/api/maternal/postnatal-visits/${id}`),

  // Children
  getChildren: (params) => api.get('/api/maternal/children', { params }),
  getChild: (id) => api.get(`/api/maternal/children/${id}`),
  createChild: (data) => api.post('/api/maternal/children', data),
  updateChild: (id, data) => api.put(`/api/maternal/children/${id}`, data),
  deleteChild: (id) => api.delete(`/api/maternal/children/${id}`),

  // Immunizations
  getImmunizations: (childId = null) => {
    const params = childId ? { child_id: childId } : {};
    return api.get('/api/maternal/immunizations', { params });
  },
  getImmunization: (id) => api.get(`/api/maternal/immunizations/${id}`),
  createImmunization: (data) => api.post('/api/maternal/immunizations', data),
  updateImmunization: (id, data) => api.put(`/api/maternal/immunizations/${id}`, data),
  deleteImmunization: (id) => api.delete(`/api/maternal/immunizations/${id}`),

  // Growth Monitoring
  getGrowthRecords: (childId) => api.get('/api/maternal/growth-monitoring', { params: { child_id: childId } }),
  getGrowthRecord: (id) => api.get(`/api/maternal/growth-monitoring/${id}`),
  createGrowthRecord: (data) => api.post('/api/maternal/growth-monitoring', data),
  updateGrowthRecord: (id, data) => api.put(`/api/maternal/growth-monitoring/${id}`, data),
  deleteGrowthRecord: (id) => api.delete(`/api/maternal/growth-monitoring/${id}`),

  // Stats
  getStats: () => api.get('/api/maternal/stats'),

  // ANC Visit Linkage
  linkOrderToANCVisit: (ancVisitId, orderData) => api.post(`/api/maternal/anc-visits/${ancVisitId}/link-order`, orderData),
  getLinkedOrders: (ancVisitId) => api.get(`/api/maternal/anc-visits/${ancVisitId}/linked-orders`),

  // Maternal Screening Alerts
  getScreeningAlerts: (params) => api.get('/api/maternal/screening-alerts', { params }),
  createScreeningAlert: (data) => api.post('/api/maternal/screening-alerts', data),
  updateScreeningAlert: (id, data) => api.put(`/api/maternal/screening-alerts/${id}`, data),
  getActiveCriticalAlerts: () => api.get('/api/maternal/screening-alerts/active-critical'),

  // ANC Orders (Direct to Lab/Radiology/Pharmacy modules)
  createANCOrders: (data) => api.post('/api/maternal/anc-orders', data),

  // Postnatal Medication Alerts
  getDuePostnatalMedications: () => api.get('/api/maternal/postnatal/due-medications'),

  // Postnatal Care - Prescriptions & Admissions
  createPostnatalPrescription: (data) => api.post('/api/maternal/postnatal/prescribe', data),
  admitMotherAndBaby: (data) => api.post('/api/maternal/postnatal/admit-mother-and-baby', data),
  getPostnatalAdmittedPatients: () => api.get('/api/maternal/postnatal/admitted-patients'),
};

// Child Development APIs
export const developmentalAPI = {
  // Assessments
  getAssessments: (params) => api.get('/api/developmental/assessments', { params }),
  getAssessment: (id) => api.get(`/api/developmental/assessments/${id}`),
  createAssessment: (data) => api.post('/api/developmental/assessments', data),
  updateAssessment: (id, data) => api.put(`/api/developmental/assessments/${id}`, data),
  getChildProgress: (childId) => api.get(`/api/developmental/children/${childId}/progress`),

  // Alerts
  getAlerts: (params) => api.get('/api/developmental/alerts', { params }),
  createAlert: (data) => api.post('/api/developmental/alerts', data),
  updateAlert: (id, data) => api.put(`/api/developmental/alerts/${id}`, data),
  getCriticalAlerts: () => api.get('/api/developmental/alerts/critical'),

  // Interventions
  getInterventions: (params) => api.get('/api/developmental/interventions', { params }),
  createIntervention: (data) => api.post('/api/developmental/interventions', data),
  updateIntervention: (id, data) => api.put(`/api/developmental/interventions/${id}`, data),

  // Statistics
  getStats: () => api.get('/api/developmental/stats'),
  getChildrenSummary: (params) => api.get('/api/developmental/children/summary', { params }),
};

// Child Health APIs (Vitamin A & Deworming)
export const childHealthAPI = {
  // Vitamin A
  getVitaminARecords: (childId) => api.get('/api/child-health/vitamin-a', { params: { child_id: childId } }),
  createVitaminARecord: (data) => api.post('/api/child-health/vitamin-a', data),
  getVitaminARecord: (id) => api.get(`/api/child-health/vitamin-a/${id}`),
  updateVitaminARecord: (id, data) => api.put(`/api/child-health/vitamin-a/${id}`, data),
  getChildVitaminAHistory: (childId) => api.get(`/api/child-health/vitamin-a/child/${childId}/history`),
  getVitaminADueList: () => api.get('/api/child-health/vitamin-a/due-list'),

  // Deworming
  getDewormingRecords: (childId) => api.get('/api/child-health/deworming', { params: { child_id: childId } }),
  createDewormingRecord: (data) => api.post('/api/child-health/deworming', data),
  getDewormingRecord: (id) => api.get(`/api/child-health/deworming/${id}`),
  updateDewormingRecord: (id, data) => api.put(`/api/child-health/deworming/${id}`, data),
  getChildDewormingHistory: (childId) => api.get(`/api/child-health/deworming/child/${childId}/history`),
  getDewormingDueList: () => api.get('/api/child-health/deworming/due-list'),

  // Stats
  getVitaminAStats: () => api.get('/api/child-health/stats/vitamin-a'),
  getDewormingStats: () => api.get('/api/child-health/stats/deworming'),
  getCombinedStats: () => api.get('/api/child-health/stats/combined'),
};

// IMNCI APIs
export const imnciAPI = {
  // Queue
  getQueue: () => api.get('/api/imnci/queue'),
  startFromQueue: (data) => api.post('/api/imnci/queue/start', data),

  // Knowledge base
  getDomains: (params) => api.get('/api/imnci/domains', { params }),
  getDomain: (domainId) => api.get(`/api/imnci/domains/${domainId}`),
  getDosageTables: (params) => api.get('/api/imnci/dosage-tables', { params }),

  // Encounters
  createEncounter: (data) => api.post('/api/imnci/encounters', data),
  listEncounters: (params) => api.get('/api/imnci/encounters', { params }),
  getEncounter: (id) => api.get(`/api/imnci/encounters/${id}`),
  updateEncounter: (id, data) => api.put(`/api/imnci/encounters/${id}`, data),
  completeEncounter: (id) => api.post(`/api/imnci/encounters/${id}/complete`),

  // Domain Assessments
  saveDomainAssessment: (encounterId, data) => api.post(`/api/imnci/encounters/${encounterId}/assess`, data),
  saveAssessment: (encounterId, data) => api.post(`/api/imnci/encounters/${encounterId}/assess`, data),
  listAssessments: (encounterId) => api.get(`/api/imnci/encounters/${encounterId}/assessments`),

  // Treatment Orders
  listTreatments: (encounterId) => api.get(`/api/imnci/encounters/${encounterId}/treatments`),
  createTreatment: (encounterId, drugId, domainAssessmentId) =>
    api.post(`/api/imnci/encounters/${encounterId}/treatments`, null, {
      params: { drug_id: drugId, domain_assessment_id: domainAssessmentId },
    }),
  administerTreatment: (treatmentId, data) => api.post(`/api/imnci/treatments/${treatmentId}/administer`, data),

  // Follow-up Tasks
  listFollowUps: (params) => api.get('/api/imnci/follow-ups', { params }),
  createFollowUp: (data) => api.post('/api/imnci/follow-ups', data),
  createFollowUpBatch: (items) => api.post('/api/imnci/follow-ups/batch', items),
  completeFollowUp: (taskId, data) => api.post(`/api/imnci/follow-ups/${taskId}/complete`, null, { params: data }),

  // Referral Note
  createReferralNote: (encounterId, data) => api.post(`/api/imnci/encounters/${encounterId}/referral`),
  generateReferral: (encounterId) => api.post(`/api/imnci/encounters/${encounterId}/referral`),
  getReferral: (encounterId) => api.get(`/api/imnci/encounters/${encounterId}/referral`),

  // Classification Engine
  classify: (data) => api.post('/api/imnci/classify', data),
  classifyDomain: (data) => api.post('/api/imnci/classify', data),
  classifyEncounter: (encounterId, ctx) => api.post(`/api/imnci/encounters/${encounterId}/classify`, ctx),
  suggestTreatments: (data) => api.post('/api/imnci/suggest-treatments', data),

  // Dosage Lookup
  dosageLookup: (data) => api.post('/api/imnci/dosage-lookup', data),
  lookupDosage: (data) => api.post('/api/imnci/dosage-lookup', data),

  // Stats
  getStats: () => api.get('/api/imnci/stats'),
};

// Radiology APIs
export const radiologyAPI = {
  // Modalities
  getModalities: (params) => api.get('/api/radiology/modalities', { params }),
  getModality: (id) => api.get(`/api/radiology/modalities/${id}`),
  createModality: (data) => api.post('/api/radiology/modalities', data),
  updateModality: (id, data) => api.put(`/api/radiology/modalities/${id}`, data),
  deleteModality: (id) => api.delete(`/api/radiology/modalities/${id}`),

  // Studies/Orders
  getStudies: (params) => api.get('/api/radiology/studies', { params }),
  getStudy: (id) => api.get(`/api/radiology/studies/${id}`),
  createStudy: (data) => api.post('/api/radiology/studies', data),
  updateStudy: (id, data) => api.put(`/api/radiology/studies/${id}`, data),

  // Report rejection / correction workflow (verifier sends reports back)
  rejectReport: (data) => api.post('/api/radiology/studies/reject-report', data),
  resubmitReport: (id, data) => api.post(`/api/radiology/studies/${id}/resubmit-report`, data),
  getScansByPatient: (patientId) => api.get(`/api/radiology/studies/patient/${patientId}`),
  deleteStudy: (id) => api.delete(`/api/radiology/studies/${id}`),
  getStudiesByPatient: (patientId) => api.get(`/api/radiology/studies/patient/${patientId}`),

  // External result upload/download
  uploadExternalResult: (studyId, formData) => api.post(`/api/radiology/studies/${studyId}/upload-external-result`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteExternalResult: (studyId) => api.delete(`/api/radiology/studies/${studyId}/external-result`),
  downloadExternalResult: (studyId) => api.get(`/api/radiology/studies/${studyId}/download-external-result`, {
    responseType: 'blob',
  }),

  // Study image upload (PACS)
  uploadStudyImages: (studyId, files) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return api.post(`/api/radiology/studies/${studyId}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteStudyImage: (studyId, imageIndex) => api.delete(`/api/radiology/studies/${studyId}/images/${imageIndex}`),

  // Archive scan images as a PDF at submission; server deletes the raw images
  uploadScanImagesPdf: (studyId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/radiology/studies/${studyId}/scan-images-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Fetch the archived scan-images PDF (authorized) for printing
  fetchScanImagesPdfBlob: (url) => api.get(url, { responseType: 'blob' }),

  // Stats
  getStats: () => api.get('/api/radiology/stats/overview'),
  getAnalyticsStats: (days = 90) => api.get('/api/radiology/stats/analytics', { params: { days } }),

  // === EXTENDED RADIOLOGY APIs ===

  // PACS Servers
  getPACSServers: () => api.get('/api/radiology/pacs'),
  createPACSServer: (data) => api.post('/api/radiology/pacs', data),
  updatePACSServer: (id, data) => api.put(`/api/radiology/pacs/${id}`, data),
  deletePACSServer: (id) => api.delete(`/api/radiology/pacs/${id}`),

  // Resources (Rooms, Equipment)
  getResources: (params) => api.get('/api/radiology/resources', { params }),
  createResource: (data) => api.post('/api/radiology/resources', data),
  updateResource: (id, data) => api.put(`/api/radiology/resources/${id}`, data),

  // Scheduling
  getSchedules: (params) => api.get('/api/radiology/schedule', { params }),
  getCalendarView: (startDate, endDate, resourceId) => api.get('/api/radiology/schedule/calendar', {
    params: { start_date: startDate, end_date: endDate, resource_id: resourceId }
  }),
  createSchedule: (data) => api.post('/api/radiology/schedule', data),
  updateSchedule: (id, data) => api.put(`/api/radiology/schedule/${id}`, data),
  checkInPatient: (scheduleId) => api.post(`/api/radiology/schedule/${scheduleId}/check-in`),

  // Contrast Media
  getContrastMedia: (params) => api.get('/api/radiology/contrast', { params }),
  createContrastMedia: (data) => api.post('/api/radiology/contrast', data),
  updateContrastMedia: (id, data) => api.put(`/api/radiology/contrast/${id}`, data),
  administerContrast: (data) => api.post('/api/radiology/contrast/administer', data),
  recordAdverseReaction: (adminId, details, severity) => api.post(`/api/radiology/contrast/administer/${adminId}/reaction`, {
    reaction_details: details,
    severity
  }),

  // Consumables
  getConsumables: (params) => api.get('/api/radiology/consumables', { params }),
  createConsumable: (data) => api.post('/api/radiology/consumables', data),
  updateConsumable: (id, data) => api.put(`/api/radiology/consumables/${id}`, data),
  recordConsumableUsage: (data) => api.post('/api/radiology/consumables/use', data),

  // Equipment Maintenance
  getMaintenanceRecords: (params) => api.get('/api/radiology/maintenance', { params }),
  createMaintenance: (data) => api.post('/api/radiology/maintenance', data),
  updateMaintenance: (id, data) => api.put(`/api/radiology/maintenance/${id}`, data),

  // Report Distribution
  getReportDistributions: (studyId) => api.get(`/api/radiology/distribution/${studyId}`),
  createDistribution: (data) => api.post('/api/radiology/distribution', data),
  notifyAllRecipients: (studyId) => api.post(`/api/radiology/distribution/${studyId}/notify-all`),
  acknowledgeReport: (distributionId) => api.post(`/api/radiology/distribution/${distributionId}/acknowledge`),

  // Prior Study Comparison
  getPriorStudies: (studyId, sameModalityOnly = false) => api.get(`/api/radiology/prior-studies/${studyId}`, {
    params: { same_modality_only: sameModalityOnly }
  }),
  linkPriorStudy: (data) => api.post('/api/radiology/prior-studies/link', data),
  unlinkPriorStudy: (linkId) => api.delete(`/api/radiology/prior-studies/link/${linkId}`),

  // Inventory Dashboard
  getInventoryDashboard: () => api.get('/api/radiology/inventory/dashboard'),
};

// Notification APIs
export const notificationAPI = {
  getNotifications: (params) => api.get('/api/notifications', { params }),
  getUnreadCount: () => api.get('/api/notifications/unread-count'),
  markAsRead: (id) => api.put(`/api/notifications/${id}/read`),
  markAllRead: () => api.put('/api/notifications/mark-all-read'),
  dismissNotification: (id) => api.delete(`/api/notifications/${id}`),
  dismissAll: () => api.delete('/api/notifications'),
};

// Staff Chat APIs (E2E encrypted; server only sees ciphertext)
export const chatAPI = {
  // Key management
  registerKey: (payload) => api.post('/api/chat/keys', payload),
  getMyKeyStatus: () => api.get('/api/chat/keys/me'),
  getUserKey: (userId) => api.get(`/api/chat/keys/${userId}`),
  // Presence
  heartbeat: () => api.post('/api/chat/heartbeat'),
  listUsers: (params) => api.get('/api/chat/users', { params }),
  // Rooms
  listRooms: () => api.get('/api/chat/rooms'),
  createRoom: (payload) => api.post('/api/chat/rooms', payload),
  getRoom: (roomId) => api.get(`/api/chat/rooms/${roomId}`),
  updateRoom: (roomId, payload) => api.patch(`/api/chat/rooms/${roomId}`, payload),
  updateMyMember: (roomId, payload) => api.patch(`/api/chat/rooms/${roomId}/members/me`, payload),
  addMembers: (roomId, payload) => api.post(`/api/chat/rooms/${roomId}/members`, payload),
  bootstrapRoomKey: (roomId, payload) => api.post(`/api/chat/rooms/${roomId}/key`, payload),
  deleteMessage: (roomId, messageId) => api.delete(`/api/chat/rooms/${roomId}/messages/${messageId}`),
  // Messages
  listMessages: (roomId, params) => api.get(`/api/chat/rooms/${roomId}/messages`, { params }),
  sendMessage: (roomId, payload) => api.post(`/api/chat/rooms/${roomId}/messages`, payload),
  markRead: (roomId) => api.post(`/api/chat/rooms/${roomId}/read`),
  unreadSummary: () => api.get('/api/chat/unread-summary'),
  // Late-key wrapping (for users invited while offline / before enabling chat)
  pendingKeyRooms: () => api.get('/api/chat/pending-key-rooms'),
  wrapKeysForMembers: (roomId, payload) => api.post(`/api/chat/rooms/${roomId}/wrap-keys`, payload),
};

// Financial Alerts APIs
export const financialAlertsAPI = {
  getSummary: () => api.get('/api/financial-alerts/summary'),
  listOutstanding: (params) => api.get('/api/financial-alerts/outstanding', { params }),
  listUpcomingPaydays: () => api.get('/api/financial-alerts/upcoming-paydays'),
  listReminderHistory: (params) => api.get('/api/financial-alerts/reminder-history', { params }),
  sendReminder: (payload) => api.post('/api/financial-alerts/send-reminder', payload),
  sendBulk: (payload) => api.post('/api/financial-alerts/send-bulk', payload),
  getSettings: () => api.get('/api/financial-alerts/settings'),
  updateSettings: (payload) => api.put('/api/financial-alerts/settings', payload),
};

// Emergency APIs
export const emergencyAPI = {
  // Triage
  createTriage: (data) => api.post('/api/emergency/triage', data),
  getTriages: (params) => api.get('/api/emergency/triage', { params }),
  getTriage: (id) => api.get(`/api/emergency/triage/${id}`),
  updateTriage: (id, data) => api.put(`/api/emergency/triage/${id}`, data),
  getTriageBoard: () => api.get('/api/emergency/triage-board'),

  // Emergency Visits
  createVisit: (data) => api.post('/api/emergency/visits', data),
  getVisits: (params) => api.get('/api/emergency/visits', { params }),
  getVisit: (id) => api.get(`/api/emergency/visits/${id}`),
  updateVisit: (id, data) => api.put(`/api/emergency/visits/${id}`, data),
  createDisposition: (visitId, data) => api.post(`/api/emergency/visits/${visitId}/disposition`, data),
  getPatientVisits: (patientId) => api.get(`/api/emergency/visits/patient/${patientId}`),

  // Admission to IPD
  admitToIPD: (visitId, data) => api.post(`/api/emergency/visits/${visitId}/admit-to-ipd`, data),

  // Medications
  administerMedication: (data) => api.post('/api/emergency/medications', data),
  getVisitMedications: (visitId) => api.get(`/api/emergency/medications/visit/${visitId}`),
  updateMedication: (id, data) => api.put(`/api/emergency/medications/${id}`, data),

  // Rx History (for Emergency patients in Clinical workspace)
  getPatientRxHistory: (patientId) => api.get(`/api/emergency/rx-history/patient/${patientId}`),

  // Emergency Vitals
  getPatientLatestEmergencyVitals: (patientId) => api.get(`/api/emergency/vitals/patient/${patientId}/latest`),
  saveEmergencyVitals: (visitId, data) => api.post(`/api/emergency/vitals/visit/${visitId}`, data),
  getEmergencyVitalsHistory: (visitId) => api.get(`/api/emergency/vitals/visit/${visitId}/history`),

  // Emergency Fluid Balance (IPD-independent)
  getEmergencyFluidBalance: (visitId, date = null) => api.get(`/api/emergency/fluid-balance/visit/${visitId}`, { params: { date } }),
  getEmergencyFluidBalanceSummary: (visitId, date = null) => api.get(`/api/emergency/fluid-balance/visit/${visitId}/summary`, { params: { date } }),
  createEmergencyFluidBalance: (visitId, data) => api.post(`/api/emergency/fluid-balance/visit/${visitId}`, data),

  // Emergency Clinical Rounds (IPD-independent lifecycle)
  getClinicalRounds: (visitId) => api.get(`/api/emergency/clinical-rounds/visit/${visitId}`),
  startClinicalRound: (data) => api.post('/api/emergency/clinical-rounds', data),
  finishClinicalRound: (roundId) => api.post(`/api/emergency/clinical-rounds/${roundId}/finish`),

  // Emergency Medication Administration (IPD-independent)
  createEmergencyMedication: (data) => api.post('/api/emergency/medications', data),

  // Emergency Sundry Usage (IPD-independent)
  getVisitSupplyUsage: (visitId) => api.get(`/api/emergency/supply-usage/visit/${visitId}`),
  recordSupplyUsage: (data) => api.post('/api/emergency/supply-usage', data),

  // Emergency Medication Dispensing with Invoice
  dispenseWithInvoice: (data) => api.post('/api/emergency/dispense-with-invoice', data),

  // Stats
  getStats: () => api.get('/api/emergency/stats/overview'),
  getWaitTimes: () => api.get('/api/emergency/stats/wait-times'),

  // ED Rooms
  getRooms: (params) => api.get('/api/emergency/rooms', { params }),
  createRoom: (data) => api.post('/api/emergency/rooms', data),
  updateRoom: (id, data) => api.put(`/api/emergency/rooms/${id}`, data),
  deleteRoom: (id) => api.delete(`/api/emergency/rooms/${id}`),
  reorderRooms: (data) => api.put('/api/emergency/rooms/reorder', data),

  // ED Beds (Bed Board)
  getBedBoard: () => api.get('/api/emergency/bed-board'),
  getRoomBeds: (roomId) => api.get(`/api/emergency/rooms/${roomId}/beds`),
  createBed: (roomId, data) => api.post(`/api/emergency/rooms/${roomId}/beds`, data),
  updateBed: (bedId, data) => api.put(`/api/emergency/beds/${bedId}`, data),
  deleteBed: (bedId) => api.delete(`/api/emergency/beds/${bedId}`),
  assignBed: (bedId, data) => api.post(`/api/emergency/beds/${bedId}/assign`, data),
  transferBed: (bedId, data) => api.post(`/api/emergency/beds/${bedId}/transfer`, data),
  dischargeBed: (bedId, emergencyVisitId) => api.delete(`/api/emergency/beds/${bedId}/assign`, { params: { emergency_visit_id: emergencyVisitId } }),

  getOutstandingOrders: (params) => api.get('/api/emergency/orders', { params }),
  getAlerts: () => api.get('/api/emergency/alerts'),
  getIncomingPatients: (params) => api.get('/api/emergency/incoming', { params }),
  createIncomingPatient: (data) => api.post('/api/emergency/incoming', data),
  updateIncomingPatient: (id, data) => api.put(`/api/emergency/incoming/${id}`, data),
  getResults: (params) => api.get('/api/emergency/results', { params }),
};

// Finance APIs
export const financeAPI = {
  // Dashboard
  getDashboard: () => api.get('/api/finance/dashboard'),

  // Accounts
  getAccounts: (params) => api.get('/api/finance/accounts', { params }),
  createAccount: (data) => api.post('/api/finance/accounts', data),
  updateAccount: (id, data) => api.put(`/api/finance/accounts/${id}`, data),
  deleteAccount: (id) => api.delete(`/api/finance/accounts/${id}`),
  recalculateBalances: () => api.post('/api/finance/accounts/recalculate-balances'),
  linkExpensesToAccounts: () => api.post('/api/finance/expenses/link-to-accounts'),

  // Journal Entries
  postJournalBatch: (data) => api.post('/api/finance/journal-entries/batch', data),

  // Expenses
  getExpenses: (params) => api.get('/api/finance/expenses', { params }),
  getExpense: (id) => api.get(`/api/finance/expenses/${id}`),
  createExpense: (data) => api.post('/api/finance/expenses', data),
  updateExpense: (id, data) => api.put(`/api/finance/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/api/finance/expenses/${id}`),

  // Budgets
  getBudgets: (params) => api.get('/api/finance/budgets', { params }),
  getBudget: (id) => api.get(`/api/finance/budgets/${id}`),
  createBudget: (data) => api.post('/api/finance/budgets', data),
  updateBudget: (id, data) => api.put(`/api/finance/budgets/${id}`, data),
  deleteBudget: (id) => api.delete(`/api/finance/budgets/${id}`),

  // Accounts Receivable
  getReceivables: (params) => api.get('/api/finance/receivables', { params }),
  recordReceivablePayment: (id, data) => api.post(`/api/finance/receivables/${id}/payment`, data),

  // Accounts Payable
  getPayables: (params) => api.get('/api/finance/payables', { params }),
  getPayable: (id) => api.get(`/api/finance/payables/${id}`),
  createPayable: (data) => api.post('/api/finance/payables', data),
  updatePayable: (id, data) => api.put(`/api/finance/payables/${id}`, data),
  deletePayable: (id) => api.delete(`/api/finance/payables/${id}`),
  recordPayablePayment: (id, data) => api.post(`/api/finance/payables/${id}/payment`, data),

  // Reports
  getProfitLoss: (params) => api.get('/api/finance/reports/profit-loss', { params }),
  getBalanceSheet: (params) => api.get('/api/finance/reports/balance-sheet', { params }),
  getARAging: (params) => api.get('/api/finance/reports/ar-aging', { params }),
  getIncomeByDepartment: (params) => api.get('/api/finance/reports/income-by-department', { params }),
  getDepartmentItemsAudit: (params) => api.get('/api/finance/reports/department-items-audit', { params }),
  getSavings: (params) => api.get('/api/finance/reports/savings', { params }),
  getCashFlowProjection: (params) => api.get('/api/finance/reports/cash-flow-projection', { params }),
};

// Purchasing APIs
export const purchasingAPI = {
  // Requisitions
  getRequisitions: (params) => api.get('/api/purchasing/requisitions', { params }),
  getRequisition: (id) => api.get(`/api/purchasing/requisitions/${id}`),
  createRequisition: (data) => api.post('/api/purchasing/requisitions', data),
  updateRequisition: (id, data) => api.put(`/api/purchasing/requisitions/${id}`, data),
  deleteRequisition: (id) => api.delete(`/api/purchasing/requisitions/${id}`),
  convertRequisitionToOrder: (id, params) => api.post(`/api/purchasing/requisitions/${id}/convert-to-order`, null, { params }),

  // Purchase Orders
  getOrders: (params) => api.get('/api/purchasing/orders', { params }),
  getOrder: (id) => api.get(`/api/purchasing/orders/${id}`),
  createOrder: (data) => api.post('/api/purchasing/orders', data),
  updateOrder: (id, data) => api.put(`/api/purchasing/orders/${id}`, data),
  deleteOrder: (id) => api.delete(`/api/purchasing/orders/${id}`),

  // GRNs
  getGRNs: (params) => api.get('/api/purchasing/grns', { params }),
  getGRN: (id) => api.get(`/api/purchasing/grns/${id}`),
  createGRN: (data) => api.post('/api/purchasing/grns', data),
  updateGRN: (id, data) => api.put(`/api/purchasing/grns/${id}`, data),
  deleteGRN: (id) => api.delete(`/api/purchasing/grns/${id}`),
  uploadGRNReceipt: (grnId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/purchasing/grns/${grnId}/upload-receipt`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Supplier Analytics
  getSupplierAnalytics: (params) => api.get('/api/purchasing/supplier-analytics', { params }),
};

// Procedures APIs
export const proceduresAPI = {
  // Catalog
  getCatalog: (params) => api.get('/api/procedures/catalog', { params }),
  getProcedure: (id) => api.get(`/api/procedures/catalog/${id}`),
  createProcedure: (data) => api.post('/api/procedures/catalog', data),
  updateProcedure: (id, data) => api.put(`/api/procedures/catalog/${id}`, data),
  deleteProcedure: (id) => api.delete(`/api/procedures/catalog/${id}`),

  // Orders
  getOrders: (params) => api.get('/api/procedures/orders', { params }),
  getOrder: (id) => api.get(`/api/procedures/orders/${id}`),
  createOrder: (data) => api.post('/api/procedures/orders', data),
  updateOrder: (id, data) => api.put(`/api/procedures/orders/${id}`, data),
  deleteOrder: (id) => api.delete(`/api/procedures/orders/${id}`),
  getPatientOrders: (patientId) => api.get(`/api/procedures/orders/patient/${patientId}`),
  getVisitOrders: (visitId) => api.get(`/api/procedures/orders/visit/${visitId}`),

  // Consent Upload
  uploadConsent: (orderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/procedures/upload-consent/${orderId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // === EXTENDED PROCEDURES APIs ===
  // Theatres
  getTheatres: (params) => api.get('/api/procedures/theatres', { params }),
  createTheatre: (data) => api.post('/api/procedures/theatres', data),
  updateTheatreStatus: (id, status) => api.put(`/api/procedures/theatres/${id}/status`, null, { params: { status } }),
  // Bookings
  getBookings: (params) => api.get('/api/procedures/bookings', { params }),
  getBookingsCalendar: (startDate, endDate, theatreId) => api.get('/api/procedures/bookings/calendar', { params: { start_date: startDate, end_date: endDate, theatre_id: theatreId } }),
  createBooking: (data) => api.post('/api/procedures/bookings', data),
  updateBooking: (id, data) => api.put(`/api/procedures/bookings/${id}`, data),
  startProcedureBooking: (id) => api.post(`/api/procedures/bookings/${id}/start`),
  completeProcedureBooking: (id) => api.post(`/api/procedures/bookings/${id}/complete`),
  // WHO Checklist
  createWHOChecklist: (data) => api.post('/api/procedures/who-checklist', data),
  getWHOChecklist: (orderId) => api.get(`/api/procedures/who-checklist/${orderId}`),
  completeSignIn: (id, data) => api.put(`/api/procedures/who-checklist/${id}/sign-in`, data),
  completeTimeOut: (id, data) => api.put(`/api/procedures/who-checklist/${id}/time-out`, data),
  completeSignOut: (id, data) => api.put(`/api/procedures/who-checklist/${id}/sign-out`, data),
  // PACU
  getPACUDashboard: () => api.get('/api/procedures/pacu/dashboard'),
  getPACUAdmissions: (params) => api.get('/api/procedures/pacu', { params }),
  admitToPACU: (data) => api.post('/api/procedures/pacu', data),
  updatePACU: (id, data) => api.put(`/api/procedures/pacu/${id}`, data),
  dischargePACU: (id, data) => api.post(`/api/procedures/pacu/${id}/discharge`, data),
  recordPACUVitals: (id, data) => api.post(`/api/procedures/pacu/${id}/vitals`, data),
  getPACUVitals: (id) => api.get(`/api/procedures/pacu/${id}/vitals`),
  updatePACUAldrete: (id, data) => api.put(`/api/procedures/pacu/${id}/aldrete`, data),
  administerPACUMedication: (id, data) => api.post(`/api/procedures/pacu/${id}/medications`, data),
  givePACUMedication: (id, data) => api.post(`/api/procedures/pacu/${id}/medications`, data),
  // Anesthesia
  createAnesthesiaRecord: (data) => api.post('/api/procedures/anesthesia', data),
  getAnesthesiaRecord: (orderId) => api.get(`/api/procedures/anesthesia/${orderId}`),
  updateAnesthesiaRecord: (id, data) => api.put(`/api/procedures/anesthesia/${id}`, data),
  recordAnesthesiaVitals: (id, data) => api.post(`/api/procedures/anesthesia/${id}/vitals`, data),
  // Instruments & Counts
  getInstrumentSets: (params) => api.get('/api/procedures/instruments', { params }),
  createInstrumentSet: (data) => api.post('/api/procedures/instruments', data),
  recordSurgicalCount: (data) => api.post('/api/procedures/counts', data),
  getSurgicalCounts: (orderId) => api.get(`/api/procedures/counts/${orderId}`),
  verifyCount: (id) => api.post(`/api/procedures/counts/${id}/verify`),
  reportMalfunction: (data) => api.post('/api/procedures/equipment-malfunction', data),
  // Waiting List
  getWaitingList: (params) => api.get('/api/procedures/waiting-list', { params }),
  getWaitingListStats: () => api.get('/api/procedures/waiting-list/stats'),
  addToWaitingList: (data) => api.post('/api/procedures/waiting-list', data),
  updateWaitingListEntry: (id, data) => api.put(`/api/procedures/waiting-list/${id}`, data),
  updateWaitingEntry: (id, data) => api.put(`/api/procedures/waiting-list/${id}`, data),
  removeFromWaitingList: (id, reason) => api.delete(`/api/procedures/waiting-list/${id}`, { params: { reason } }),
  // Templates
  getTemplates: (params) => api.get('/api/procedures/templates', { params }),
  createTemplate: (data) => api.post('/api/procedures/templates', data),
  getTemplate: (id) => api.get(`/api/procedures/templates/${id}`),
  // Media
  uploadProcedureMedia: (orderId, file, metadata = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('procedure_order_id', orderId);
    if (metadata.title) formData.append('title', metadata.title);
    if (metadata.category) formData.append('category', metadata.category);
    if (metadata.description) formData.append('description', metadata.description);
    if (metadata.annotations) formData.append('annotations', metadata.annotations);
    return api.post('/api/procedures/media', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getProcedureMedia: (orderId) => api.get(`/api/procedures/media/${orderId}`),
  deleteProcedureMedia: (id) => api.delete(`/api/procedures/media/${id}`),
  // Supplies
  getSupplyKits: (params) => api.get('/api/procedures/supply-kits', { params }),
  createSupplyKit: (data) => api.post('/api/procedures/supply-kits', data),
  recordSupplyUsage: (data) => api.post('/api/procedures/supplies/usage', data),
  getProcedureSupplies: (orderId) => api.get(`/api/procedures/supplies/${orderId}`),
  getProcedureCost: (orderId) => api.get(`/api/procedures/supplies/${orderId}/cost`),
  // Referrals
  getExternalReferrals: (params) => api.get('/api/procedures/referrals', { params }),
  createExternalReferral: (data) => api.post('/api/procedures/referrals', data),
  updateExternalReferral: (id, data) => api.put(`/api/procedures/referrals/${id}`, data),
  // Post-Op Care
  getPostOpOrderSets: (params) => api.get('/api/procedures/post-op-order-sets', { params }),
  createPostOpOrderSet: (data) => api.post('/api/procedures/post-op-order-sets', data),
  createPostOpCareRecord: (data) => api.post('/api/procedures/post-op-care', data),
  getPostOpCareRecords: (orderId) => api.get(`/api/procedures/post-op-care/${orderId}`),
  createWoundCareRecord: (data) => api.post('/api/procedures/wound-care', data),
  getWoundCareRecords: (orderId) => api.get(`/api/procedures/wound-care/${orderId}`),
  scheduleFollowUp: (data) => api.post('/api/procedures/follow-ups', data),
  scheduleProcedureFollowUp: (data) => api.post('/api/procedures/follow-ups', data),
  completeFollowUp: (id, data) => api.put(`/api/procedures/follow-ups/${id}`, data),
  getProcedureFollowUps: (orderId) => api.get(`/api/procedures/follow-ups/${orderId}`),
  // Dashboard
  getTheatreDashboard: (date) => api.get('/api/procedures/dashboard/theatre', { params: { target_date: date } }),
  getAnalyticsStats: (days = 90) => api.get('/api/procedures/stats/analytics', { params: { days } }),
};

// Department APIs
export const departmentAPI = {
  getAll: (params) => api.get('/api/departments', { params }),
  getById: (id) => api.get(`/api/departments/${id}`),
  create: (data) => api.post('/api/departments', data),
  update: (id, data) => api.put(`/api/departments/${id}`, data),
  delete: (id) => api.delete(`/api/departments/${id}`),
  getTransactionPoints: () => api.get('/api/departments/transaction-points'),
  getRevenueRules: () => api.get('/api/departments/revenue-rules'),
};

// Savings APIs
export const savingsAPI = {
  createDeposit: (data) => api.post('/api/savings/deposits', data),
  getDeposits: (params) => api.get('/api/savings/deposits', { params }),
};

// Follow-up APIs
export const followupAPI = {
  // Get all follow-ups with filters
  getAll: (params) => api.get('/api/followups', { params }),
  getById: (id) => api.get(`/api/followups/${id}`),
  create: (data) => api.post('/api/followups', data),
  update: (id, data) => api.put(`/api/followups/${id}`, data),
  delete: (id) => api.delete(`/api/followups/${id}`),

  // Daily calls and birthday reminders
  getDailyFollowups: (targetDate) => api.get('/api/followups/daily', { params: { target_date: targetDate } }),
  getBirthdayReminders: (daysAhead = 7) => api.get('/api/followups/birthdays', { params: { days_ahead: daysAhead } }),

  // Actions
  complete: (id, data) => api.put(`/api/followups/${id}/complete`, data),
  reschedule: (id, data) => api.put(`/api/followups/${id}/reschedule`, data),
};

// Diagnosis Catalog APIs
export const diagnosisAPI = {
  // Get all diagnoses with filtering and search
  getAll: (params) => api.get('/api/diagnosis-catalog', { params }),
  getById: (id) => api.get(`/api/diagnosis-catalog/${id}`),
  create: (data) => api.post('/api/diagnosis-catalog', data),
  update: (id, data) => api.put(`/api/diagnosis-catalog/${id}`, data),
  delete: (id) => api.delete(`/api/diagnosis-catalog/${id}`),

  // Utility endpoints
  getCount: (params) => api.get('/api/diagnosis-catalog/count', { params }),
  getCategories: () => api.get('/api/diagnosis-catalog/categories'),
  bulkCreate: (diagnoses) => api.post('/api/diagnosis-catalog/bulk-create', diagnoses),

  // Quick-add (clinical users) — creates a tenant-scoped diagnosis on the fly
  // when the doctor cannot find the one they need in the catalog.
  quickAdd: (data) => api.post('/api/diagnosis-catalog/quick-add', data),

  // Search helper
  search: (query, limit = 50) => api.get('/api/diagnosis-catalog', {
    params: { query, limit, is_active: true }
  }),
};

// Signs & Symptoms Catalog APIs
export const signsSymptomsAPI = {
  // Get all symptoms with filtering and search
  getAll: (params) => api.get('/api/signs-symptoms', { params }),
  getById: (id) => api.get(`/api/signs-symptoms/${id}`),
  create: (data) => api.post('/api/signs-symptoms', data),
  update: (id, data) => api.put(`/api/signs-symptoms/${id}`, data),
  delete: (id) => api.delete(`/api/signs-symptoms/${id}`),

  // Utility endpoints
  getCount: (params) => api.get('/api/signs-symptoms/count', { params }),
  getCategories: () => api.get('/api/signs-symptoms/categories'),
  getBodySystems: () => api.get('/api/signs-symptoms/body-systems'),
  bulkCreate: (symptoms) => api.post('/api/signs-symptoms/bulk-create', symptoms),
  // Quick-add from the clinical workspace (auto-generates CUST.NNNN code, idempotent by name)
  quickAdd: (data) => api.post('/api/signs-symptoms/quick-add', data),

  // Search helper
  search: (query, limit = 50) => api.get('/api/signs-symptoms', {
    params: { query, limit, is_active: true }
  }),
};

// Organization Settings APIs
export const organizationAPI = {
  // Settings (uses /api/organization - singular)
  getSettings: () => api.get('/api/organization/settings'),
  updateSettings: (data) => api.put('/api/organization/settings', data),
  createSettings: (data) => api.post('/api/organization/settings', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/organization/logo', formData);
  },
  createBackup: (data) => api.post('/api/organization/backup', data),
  getBackupModules: () => api.get('/api/organization/backup/modules'),
  restoreBackup: (filename, data) => api.post(`/api/organization/backups/${filename}/restore`, data),
  getBackups: () => api.get('/api/organization/backups'),
  deleteBackup: (filename) => api.delete(`/api/organization/backups/${filename}`),
  verifyBackup: (filename) => api.get(`/api/organization/backups/${filename}/verify`),
  getRestoreInstructions: () => api.get('/api/organization/restore-instructions'),
  getStorageStats: () => api.get('/api/organization/storage-stats'),
  getSetupStatus: () => api.get('/api/organization/setup-status'),

  // Module Settings
  getModuleSettings: () => api.get('/api/organization/module-settings'),
  updateModuleSettings: (data) => api.put('/api/organization/module-settings', data),

  // Catalog Export / Import
  getCatalogItems: () => api.get('/api/organization/catalog-items'),
  exportCatalog: (data) => api.post('/api/organization/catalog-export', data),
  importCatalog: (data) => api.post('/api/organization/catalog-import', data),

  // Full Patient-Data Export / Import (entire tenant clone)
  exportPatientData: (data) => api.post('/api/organization/patient-data-export', data),
  importPatientData: (data) => api.post('/api/organization/patient-data-import', data),
  getDataExportPreview: () => api.get('/api/organization/data-export/preview'),

  // Selective Module Export / Import
  getExportModules: () => api.get('/api/organization/data-export/modules'),
  getModulePreview: (data) => api.post('/api/organization/data-export/module-preview', data),
  exportSelective: (data) => api.post('/api/organization/data-export/selective', data),
  getImportPreview: (data) => api.post('/api/organization/data-import/preview', data),

  // Multi-tenant features (uses /api/organizations - plural)
  getStats: () => api.get('/api/organizations/stats'),
  search: (query) => api.get('/api/organizations/search', { params: { q: query } }),
  verifyAccess: (organizationId) => api.get(`/api/organizations/verify-access/${organizationId}`),
};

// Referral Centres APIs
export const referralCentreAPI = {
  list: (params) => api.get('/api/referral-centres', { params }),
  create: (data) => api.post('/api/referral-centres', data),
  update: (id, data) => api.put(`/api/referral-centres/${id}`, data),
  delete: (id) => api.delete(`/api/referral-centres/${id}`),
};

// Workforce APIs (HR, Roster, Attendance, Leave, Performance, CME, Payroll, Allowances/Tips, Audit)
export const workforceAPI = {
  // Overview
  getOverview: () => api.get('/api/workforce/overview'),

  // Employees
  listEmployees: (params) => api.get('/api/workforce/employees', { params }),
  getEmployee: (id) => api.get(`/api/workforce/employees/${id}`),
  createEmployee: (data) => api.post('/api/workforce/employees', data),
  createEmployeeWithAccount: (data) => api.post('/api/workforce/employees/with-account', data),
  updateEmployee: (id, data) => api.put(`/api/workforce/employees/${id}`, data),
  uploadEmployeePhoto: (id, file) => {
    const data = new FormData();
    data.append('photo', file);
    return api.post(`/api/workforce/employees/${id}/photo`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Shift templates
  listShiftTemplates: () => api.get('/api/workforce/shift-templates'),
  createShiftTemplate: (data) => api.post('/api/workforce/shift-templates', data),
  updateShiftTemplate: (id, data) => api.put(`/api/workforce/shift-templates/${id}`, data),
  deleteShiftTemplate: (id) => api.delete(`/api/workforce/shift-templates/${id}`),

  // Roster periods + assignments
  listRosterStaff: () => api.get('/api/workforce/roster/staff'),
  // Read-only duty summary for scheduling surfaces (appointments calendar)
  getOnDutyDoctors: (params) => api.get('/api/workforce/roster/on-duty', { params }),
  listRosterPeriods: () => api.get('/api/workforce/roster-periods'),
  createRosterPeriod: (data) => api.post('/api/workforce/roster-periods', data),
  publishRosterPeriod: (id) => api.post(`/api/workforce/roster-periods/${id}/publish`),
  listAssignments: (params) => api.get('/api/workforce/assignments', { params }),
  createAssignment: (data) => api.post('/api/workforce/assignments', data),
  bulkCreateAssignments: (data) => api.post('/api/workforce/assignments/bulk', data),
  deleteAssignment: (id) => api.delete(`/api/workforce/assignments/${id}`),

  // Swaps
  listSwaps: (params) => api.get('/api/workforce/swaps', { params }),
  createSwap: (data) => api.post('/api/workforce/swaps', data),
  decideSwap: (id, data) => api.post(`/api/workforce/swaps/${id}/decision`, data),

  // Attendance
  clockIn: (data) => api.post('/api/workforce/attendance/clock-in', data || {}),
  clockOut: (data) => api.post('/api/workforce/attendance/clock-out', data || {}),
  attendanceToday: () => api.get('/api/workforce/attendance/today'),
  listAttendance: (params) => api.get('/api/workforce/attendance', { params }),
  getAttendanceSettings: () => api.get('/api/workforce/attendance/settings'),
  updateAttendanceSettings: (data) => api.put('/api/workforce/attendance/settings', data),

  // Leave
  listLeaveTypes: () => api.get('/api/workforce/leave-types'),
  createLeaveType: (data) => api.post('/api/workforce/leave-types', data),
  listLeaveRequests: (params) => api.get('/api/workforce/leave-requests', { params }),
  createLeaveRequest: (data) => api.post('/api/workforce/leave-requests', data),
  decideLeave: (id, data) => api.post(`/api/workforce/leave-requests/${id}/decision`, data),
  uploadLeaveAttachment: (file) => {
    const data = new FormData();
    data.append('attachment', file);
    return api.post('/api/workforce/leave-requests/attachment', data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadLeaveAttachment: (id) => api.get(`/api/workforce/leave-requests/${id}/attachment`, { responseType: 'blob' }),
  listLeaveBalances: (params) => api.get('/api/workforce/leave-balances', { params }),

  // Performance
  listMetrics: () => api.get('/api/workforce/performance/metrics'),
  createMetric: (data) => api.post('/api/workforce/performance/metrics', data),
  leaderboard: (params) => api.get('/api/workforce/performance/leaderboard', { params }),
  recomputeSnapshots: (period_start, period_end) =>
    api.post(`/api/workforce/performance/snapshots/recompute`, null, { params: { period_start, period_end } }),
  listSnapshots: (params) => api.get('/api/workforce/performance/snapshots', { params }),
  listReviews: (params) => api.get('/api/workforce/performance/reviews', { params }),
  createReview: (data) => api.post('/api/workforce/performance/reviews', data),

  // CME
  listCMEEvents: () => api.get('/api/workforce/cme/events'),
  createCMEEvent: (data) => api.post('/api/workforce/cme/events', data),
  markCMEAttendance: (data) => api.post('/api/workforce/cme/attendance', data),
  cmeLeaderboard: (params) => api.get('/api/workforce/cme/leaderboard', { params }),

  // Compensation & Payroll
  listPayrollPayGroups: () => api.get('/api/workforce/payroll/pay-groups'),
  listCompensation: (params) => api.get('/api/workforce/compensation', { params }),
  createCompensation: (data) => api.post('/api/workforce/compensation', data),
  listPayrollPeriods: () => api.get('/api/workforce/payroll/periods'),
  createPayrollPeriod: (data) => api.post('/api/workforce/payroll/periods', data),
  runPayroll: (periodId) => api.post(`/api/workforce/payroll/periods/${periodId}/run`),
  markPaid: (periodId) => api.post(`/api/workforce/payroll/periods/${periodId}/mark-paid`),
  listPayslips: (params) => api.get('/api/workforce/payroll/payslips', { params }),
  upcomingPayroll: () => api.get('/api/workforce/payroll/upcoming'),

  // Allowances & Tips
  listAllowanceTypes: () => api.get('/api/workforce/allowance-types'),
  createAllowanceType: (data) => api.post('/api/workforce/allowance-types', data),
  listAllowances: (params) => api.get('/api/workforce/allowances', { params }),
  createAllowance: (data) => api.post('/api/workforce/allowances', data),
  approveAllowance: (id, data) => api.post(`/api/workforce/allowances/${id}/approve`, data || {}),
  payAllowance: (id, data) => api.post(`/api/workforce/allowances/${id}/pay`, data || {}),
  cancelAllowance: (id) => api.post(`/api/workforce/allowances/${id}/cancel`),
  listTips: (params) => api.get('/api/workforce/tips', { params }),
  createTip: (data) => api.post('/api/workforce/tips', data),
  approveTip: (id, data) => api.post(`/api/workforce/tips/${id}/approve`, data || {}),
  payTip: (id, data) => api.post(`/api/workforce/tips/${id}/pay`, data || {}),
  cancelTip: (id) => api.post(`/api/workforce/tips/${id}/cancel`),

  // Audit (workforce surface)
  verifyAuditChain: () => api.get('/api/workforce/audit/verify-chain'),
  phiAccessFor: (patientId) => api.get(`/api/workforce/audit/phi-access/${patientId}`),
};

// Audit Trail APIs
export const auditAPI = {
  getLogs: (params) => api.get('/api/audit/logs', { params }),
  getLog: (id) => api.get(`/api/audit/logs/${id}`),
  getStats: (params) => api.get('/api/audit/stats', { params }),
  getEntityTypes: () => api.get('/api/audit/entity-types'),
  getActions: () => api.get('/api/audit/actions'),
};

// Two-Factor Authentication APIs
export const twoFactorAPI = {
  setup: () => api.post('/api/2fa/setup'),
  verifySetup: (token) => api.post('/api/2fa/verify-setup', { token }),
  verify: (token) => api.post('/api/2fa/verify', { token }),
  disable: (password) => api.post('/api/2fa/disable', { password }),
  regenerateBackupCodes: (password) => api.post('/api/2fa/regenerate-backup-codes', { password }),
  getStatus: () => api.get('/api/2fa/status'),
  getRequiredRoles: () => api.get('/api/2fa/required-roles'),
  // Admin-only: manage 2FA for other users
  adminReset: (userId, reason) => api.post('/api/2fa/admin/reset', { user_id: userId, reason }),
  adminSetRequired: (userId, required) => api.post('/api/2fa/admin/require', { user_id: userId, required }),
};

// Lab Test Kits APIs
export const labKitsAPI = {
  getAll: (params) => api.get('/api/lab-kits', { params }),
  getById: (id) => api.get(`/api/lab-kits/${id}`),
  create: (data) => api.post('/api/lab-kits', data),
  update: (id, data) => api.put(`/api/lab-kits/${id}`, data),
  delete: (id) => api.delete(`/api/lab-kits/${id}`),

  // Batch management
  addBatch: (data) => api.post('/api/lab-kits/batches', data),
  getBatches: (kitId) => api.get(`/api/lab-kits/batches/${kitId}`),

  // Alerts
  getAlerts: (params) => api.get('/api/lab-kits/alerts/', { params }),
  checkStockLevels: () => api.post('/api/lab-kits/alerts/check'),
};

// Drug Safety APIs
export const drugSafetyAPI = {
  // Check interactions
  checkInteractions: (data) => api.post('/api/drug-safety/check-interactions', data),

  // Validate dosage
  validateDosage: (data) => api.post('/api/drug-safety/validate-dosage', data),

  // Comprehensive check
  comprehensiveCheck: (data) => api.post('/api/drug-safety/comprehensive-check', data),

  // Drug Interactions Management
  createInteraction: (data) => api.post('/api/drug-safety/interactions', data),
  getInteractions: (params) => api.get('/api/drug-safety/interactions', { params }),
  getInteraction: (id) => api.get(`/api/drug-safety/interactions/${id}`),
  updateInteraction: (id, data) => api.put(`/api/drug-safety/interactions/${id}`, data),
  deleteInteraction: (id) => api.delete(`/api/drug-safety/interactions/${id}`),
  getInteractionsByDrug: (drugId) => api.get(`/api/drug-safety/interactions/drug/${drugId}`),

  // Dosage Ranges Management
  createDosageRange: (data) => api.post('/api/drug-safety/dosage-ranges', data),
  getDosageRanges: (params) => api.get('/api/drug-safety/dosage-ranges', { params }),
  getDosageRange: (id) => api.get(`/api/drug-safety/dosage-ranges/${id}`),
  updateDosageRange: (id, data) => api.put(`/api/drug-safety/dosage-ranges/${id}`, data),
  deleteDosageRange: (id) => api.delete(`/api/drug-safety/dosage-ranges/${id}`),
  getDosageRangesByDrug: (drugId) => api.get(`/api/drug-safety/dosage-ranges/drug/${drugId}`),
};

// Superadmin APIs (Platform Management)
export const superadminAPI = {
  // Tenant Management
  getTenantsOverview: () => api.get('/api/superadmin/tenants/overview'),
  getTenantDetails: (tenantId) => api.get(`/api/superadmin/tenants/${tenantId}/details`),
  getPlatformStats: () => api.get('/api/superadmin/stats'),
  createTenant: (data) => api.post('/api/superadmin/tenants', data),
  updateTenant: (tenantId, data) => api.put(`/api/superadmin/tenants/${tenantId}`, data),
  sendTenantPasswordReset: (tenantId) => api.post(`/api/superadmin/tenants/${tenantId}/send-password-reset`),
  updateTenantStatus: (tenantId, status) => api.put(`/api/superadmin/tenants/${tenantId}/status`, { status }),
  suspendTenant: (tenantId) => api.post(`/api/superadmin/tenants/${tenantId}/suspend`),
  activateTenant: (tenantId) => api.post(`/api/superadmin/tenants/${tenantId}/activate`),
  deleteTenant: (tenantId) => api.delete(`/api/superadmin/tenants/${tenantId}`),

  // Demo request review
  getDemoRequests: (params) => api.get('/api/superadmin/demo-requests', { params }),
  approveDemoRequest: (requestId, durationDays) => api.post(`/api/superadmin/demo-requests/${requestId}/approve`, { duration_days: durationDays }),
  rejectDemoRequest: (requestId, reason = null) => api.post(`/api/superadmin/demo-requests/${requestId}/reject`, { reason }),
  resendDemoActivation: (requestId) => api.post(`/api/superadmin/demo-requests/${requestId}/resend`),

  // Issues/Feedback Management
  getIssues: (params) => api.get('/api/superadmin/issues', { params }),
  deleteIssue: (issueId) => api.delete(`/api/superadmin/issues/${issueId}`),
  updateIssueStatus: (issueId, status) => api.patch(`/api/superadmin/issues/${issueId}/status`, null, { params: { status } }),
  replyToIssue: (issueId, reply) => api.post(`/api/superadmin/issues/${issueId}/reply`, { reply }),
  toggleIssuePublic: (issueId) => api.patch(`/api/superadmin/issues/${issueId}/public`),

  // Usability Analytics
  getUsabilityAnalytics: (days = 30) => api.get('/api/superadmin/analytics/usability', { params: { days } }),
  getTenantActivity: (days = 30) => api.get('/api/superadmin/analytics/tenant-activity', { params: { days } }),

  // Revenue Analytics
  getRevenueAnalytics: () => api.get('/api/superadmin/analytics/revenue'),

  // Users Tab — platform-wide user monitoring
  getUsersOverview: () => api.get('/api/superadmin/users/overview'),
  listAllUsers: (params) => api.get('/api/superadmin/users', { params }),

  // Traffic Tab — site visit analytics
  getTrafficAnalytics: (days = 30, tz = null) => api.get('/api/superadmin/analytics/traffic', {
    params: { days, tz: tz || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' }
  }),

  // Demo Cleanup
  getPendingDemoCleanup: () => api.get('/api/superadmin/demo-cleanup/pending'),
  runDemoCleanup: () => api.post('/api/superadmin/demo-cleanup/run', null, { params: { force: true } }),

  // Plan Simulation (superadmin testing)
  getSimulatedPlan: () => api.get('/api/superadmin/simulate-plan'),
  setSimulatedPlan: (plan) => api.post('/api/superadmin/simulate-plan', null, { params: { plan } }),
  resetSimulatedPlan: () => api.delete('/api/superadmin/simulate-plan'),
};

// Follow-up Enhanced APIs
export const followupEnhancedAPI = {
  // Enhanced Follow-up Management
  create: (data) => api.post('/api/followup-enhanced/', data),
  getPatientFollowups: (patientId, params) => api.get(`/api/followup-enhanced/patient/${patientId}`, { params }),
  getOverdue: (params) => api.get('/api/followup-enhanced/overdue', { params }),
  getUpcoming: (params) => api.get('/api/followup-enhanced/upcoming', { params }),
  getDueToday: () => api.get('/api/followup-enhanced/due/today'),
  getDashboard: () => api.get('/api/followup-enhanced/statistics'),
  bulkSchedule: (data) => api.post('/api/followup-enhanced/bulk-schedule', data),
  getChronicCare: (params) => api.get('/api/followup-enhanced/chronic-care', { params }),
  linkToAppointment: (followupId, appointmentId) => api.post(`/api/followup-enhanced/${followupId}/link-appointment`, { appointment_id: appointmentId }),
  getStats: (params) => api.get('/api/followup-enhanced/stats', { params }),
};

// OTLT (One-Time Login Token) APIs
export const otltAPI = {
  exchangeToken: (token) => api.post('/api/otlt/exchange-otlt', null, { params: { token } }),
  generateLoginUrl: (tenantId, username) => api.get(`/api/otlt/generate-login-url/${tenantId}/${username}`),
};

// Diagnosis Management APIs
export const diagnosisManagementAPI = {
  verify: (data) => api.post('/api/diagnosis-management/verify', data),
  getPatientHistory: (patientId) => api.get(`/api/diagnosis-management/patient/${patientId}/history`),
  getStats: (days = 30) => api.get('/api/diagnosis-management/stats', { params: { days } }),
  batchCreate: (data) => api.post('/api/diagnosis-management/batch', data),
  getFrequent: (limit = 20) => api.get('/api/diagnosis-management/frequent', { params: { limit } }),
  searchWithSuggestions: (query) => api.get('/api/diagnosis-management/search-with-suggestions', { params: { q: query } }),

  // ICD-10 endpoints
  searchICD10: (query, limit = 20) => api.get('/api/diagnosis-management/icd10/search', { params: { query, limit } }),
  getICD10Categories: () => api.get('/api/diagnosis-management/icd10/categories'),
  getICD10ByCode: (code) => api.get(`/api/diagnosis-management/icd10/${code}`),

  // Differential & Analytics
  getAnalyticsCommon: (params) => api.get('/api/diagnosis-management/analytics/common', { params }),
  getDifferentialSuggestions: (symptoms, age, gender) => api.get('/api/diagnosis-management/differential/suggestions', {
    params: { symptoms, age, gender }
  }),

  // Patient-specific endpoints
  getPatientActiveDiagnoses: (patientId) => api.get(`/api/diagnosis-management/patient/${patientId}/active`),
  getPatientRecurrentDiagnoses: (patientId) => api.get(`/api/diagnosis-management/patient/${patientId}/recurrent`),

  // CRUD
  updateDiagnosis: (diagnosisId, data) => api.put(`/api/diagnosis-management/${diagnosisId}`, data),
  resolveDiagnosis: (diagnosisId, data) => api.post(`/api/diagnosis-management/${diagnosisId}/resolve`, data),
};

// Finance Extended APIs (for Accounts module)
export const financeExtendedAPI = {
  // Journal Entries
  getJournalEntries: (params) => api.get('/api/finance/journal-entries', { params }),
  getJournalEntry: (id) => api.get(`/api/finance/journal-entries/${id}`),

  // Trial Balance & General Ledger
  getTrialBalance: (params) => api.get('/api/finance/reports/trial-balance', { params }),
  getGeneralLedger: (params) => api.get('/api/finance/reports/general-ledger', { params }),

  // Accounts Receivable
  getAccountsReceivable: (params) => api.get('/api/finance/accounts-receivable', { params }),
  createReceivable: (data) => api.post('/api/finance/accounts-receivable', data),
  recordReceivablePayment: (id, data) => api.post(`/api/finance/accounts-receivable/${id}/payment`, data),

  // Cash Flow
  getCashFlow: (params) => api.get('/api/finance/reports/cash-flow', { params }),

  // Budget Management
  getBudgets: (params) => api.get('/api/finance/budgets', { params }),
  createBudget: (data) => api.post('/api/finance/budgets', data),
  getBudgetVsActual: (params) => api.get('/api/finance/reports/budget-vs-actual', { params }),
};

// Advanced Inventory APIs
export const inventoryAdvancedAPI = {
  // Reorder Management
  getReorderAlerts: (params) => api.get('/api/inventory/reorder-alerts', { params }),
  createPurchaseRequest: (data) => api.post('/api/inventory/purchase-requests', data),
  getPurchaseRequests: (params) => api.get('/api/inventory/purchase-requests', { params }),

  // Supplier Analytics
  getSupplierPerformance: (supplierId) => api.get(`/api/inventory/suppliers/${supplierId}/performance`),
  getSupplierComparison: () => api.get('/api/inventory/reports/supplier-comparison'),

  // Inventory Analysis
  getABCAnalysis: (params) => api.get('/api/inventory/reports/abc-analysis', { params }),
  getExpiryForecast: (days = 90) => api.get('/api/inventory/reports/expiry-forecast', { params: { days } }),
  getDeadStock: (params) => api.get('/api/inventory/reports/dead-stock', { params }),
  getTurnoverAnalysis: (params) => api.get('/api/inventory/reports/turnover-analysis', { params }),

  // Stock Transfers
  createTransfer: (data) => api.post('/api/inventory/transfers', data),
  getTransfers: (params) => api.get('/api/inventory/transfers', { params }),
  approveTransfer: (id) => api.post(`/api/inventory/transfers/${id}/approve`),

  // Stock Counts
  createStockCount: (data) => api.post('/api/inventory/stock-counts', data),
  getStockCounts: (params) => api.get('/api/inventory/stock-counts', { params }),
  getStockCountBatches: (params) => api.get('/api/inventory/stock-count-batches', { params }),
  submitStockCount: (id, data) => api.post(`/api/inventory/stock-counts/${id}/submit`, data),
  reconcileStockCount: (id) => api.post(`/api/inventory/stock-counts/${id}/reconcile`),
  getStockCountItems: (id) => api.get(`/api/inventory/stock-counts/${id}/items`),
  updateStockCountItems: (id, data) => api.put(`/api/inventory/stock-counts/${id}/items`, data),
  deleteStockCount: (id) => api.delete(`/api/inventory/stock-counts/${id}`),
};

// Blood Bank APIs
export const bloodBankAPI = {
  // Dashboard
  getDashboard: () => api.get('/api/blood-bank/dashboard'),

  // Donors
  getDonors: (params) => api.get('/api/blood-bank/donors', { params }),
  getDonor: (id) => api.get(`/api/blood-bank/donors/${id}`),
  createDonor: (data) => api.post('/api/blood-bank/donors', data),
  updateDonor: (id, data) => api.put(`/api/blood-bank/donors/${id}`, data),
  checkDonorEligibility: (id) => api.get(`/api/blood-bank/donors/${id}/eligibility`),
  getDonorDonations: (id) => api.get(`/api/blood-bank/donors/${id}/donations`),

  // Donations
  getDonations: (params) => api.get('/api/blood-bank/donations', { params }),
  createDonation: (data) => api.post('/api/blood-bank/donations', data),
  processDonation: (id) => api.post(`/api/blood-bank/donations/${id}/process`),

  // Inventory
  getInventory: (params) => api.get('/api/blood-bank/inventory', { params }),
  addInventoryStock: (data) => api.post('/api/blood-bank/inventory', data),
  getExpiringInventory: (days) => api.get('/api/blood-bank/inventory/expiring', { params: { days } }),
  updateComponent: (id, data) => api.put(`/api/blood-bank/components/${id}`, data),
  batchUpdateComponents: (data) => api.put('/api/blood-bank/components/batch', data),

  // Requests
  getRequests: (params) => api.get('/api/blood-bank/requests', { params }),
  createRequest: (data) => api.post('/api/blood-bank/requests', data),
  updateRequest: (id, data) => api.put(`/api/blood-bank/requests/${id}`, data),
  getCompatibleUnits: (requestId) => api.get(`/api/blood-bank/requests/${requestId}/compatible-units`),

  // Crossmatch
  createCrossmatch: (data) => api.post('/api/blood-bank/crossmatch', data),
  updateCrossmatch: (id, data) => api.put(`/api/blood-bank/crossmatch/${id}`, data),

  // Transfusions
  getTransfusions: (params) => api.get('/api/blood-bank/transfusions', { params }),
  createTransfusion: (data) => api.post('/api/blood-bank/transfusions', data),
  updateTransfusion: (id, data) => api.put(`/api/blood-bank/transfusions/${id}`, data),

  // Temperature Monitoring
  getTemperatureLogs: (params) => api.get('/api/blood-bank/temperature-logs', { params }),
  createTemperatureLog: (data) => api.post('/api/blood-bank/temperature-logs', data),
  getStorageUnits: () => api.get('/api/blood-bank/storage-units'),
  createStorageUnit: (data) => api.post('/api/blood-bank/storage-units', data),
  updateStorageUnit: (id, data) => api.put(`/api/blood-bank/storage-units/${id}`, data),
  deleteStorageUnit: (id) => api.delete(`/api/blood-bank/storage-units/${id}`),

  // Campaigns
  getCampaigns: (params) => api.get('/api/blood-bank/campaigns', { params }),
  createCampaign: (data) => api.post('/api/blood-bank/campaigns', data),
  updateCampaign: (id, data) => api.put(`/api/blood-bank/campaigns/${id}`, data),

  // Compatibility
  getCompatibility: (bloodType) => api.get(`/api/blood-bank/compatibility/${bloodType}`),
};

// Family Planning / ADRH APIs
export const familyPlanningAPI = {
  // Dashboard & Stats
  getDashboardStats: () => api.get('/api/family-planning/dashboard/stats'),

  // Clients
  getClients: (params) => api.get('/api/family-planning/clients', { params }),
  getClient: (id) => api.get(`/api/family-planning/clients/${id}`),
  createClient: (data) => api.post('/api/family-planning/clients', data),
  updateClient: (id, data) => api.put(`/api/family-planning/clients/${id}`, data),

  // MEC Eligibility Check
  checkMECEligibility: (data) => api.post('/api/family-planning/mec-check', data),

  // Screenings
  createScreening: (data) => api.post('/api/family-planning/screenings', data),
  getClientScreenings: (clientId) => api.get(`/api/family-planning/screenings/${clientId}`),

  // Method Administration
  getAdministrations: (params) => api.get('/api/family-planning/administrations', { params }),
  createAdministration: (data) => api.post('/api/family-planning/administrations', data),
  updateAdministration: (id, data) => api.put(`/api/family-planning/administrations/${id}`, data),

  // Upcoming & Overdue
  getUpcoming: (daysAhead = 7) => api.get('/api/family-planning/upcoming', { params: { days_ahead: daysAhead } }),
  getOverdue: () => api.get('/api/family-planning/overdue'),

  // Follow-ups
  createFollowUp: (data) => api.post('/api/family-planning/follow-ups', data),
  completeFollowUp: (id, data) => api.put(`/api/family-planning/follow-ups/${id}/complete`, data),
  getPendingFollowUps: () => api.get('/api/family-planning/follow-ups/pending'),

  // Method Information
  getMethods: () => api.get('/api/family-planning/methods'),

  // FP Inventory Summary (live stock & price per method)
  getInventorySummary: (params) => api.get('/api/family-planning/inventory/summary', { params }),
};

// Insurance/Claims APIs
export const insuranceAPI = {
  // Claims
  getClaims: (params) => api.get('/api/insurance/claims', { params }),
  getClaim: (id) => api.get(`/api/insurance/claims/${id}`),
  createClaim: (data) => api.post('/api/insurance/claims', data),
  updateClaim: (id, data) => api.put(`/api/insurance/claims/${id}`, data),
  deleteClaim: (id) => api.delete(`/api/insurance/claims/${id}`),

  // Actions
  submitClaim: (id) => api.post(`/api/insurance/claims/${id}/submit`),
  appealClaim: (id, reason) => api.post(`/api/insurance/claims/${id}/appeal`, null, { params: { appeal_reason: reason } }),
  markSubmissionBatch: (claimIds, batchRef) => api.post('/api/insurance/claims/submission-batch', { claim_ids: claimIds, batch_ref: batchRef }),
  resolveResponsibility: (id, data) => api.put(`/api/insurance/claims/${id}/responsibility`, data),

  // Claim attachments (card copies, pre-auth letters, insurer correspondence)
  getClaimAttachments: (id) => api.get(`/api/insurance/claims/${id}/attachments`),
  uploadClaimAttachment: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/insurance/claims/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteClaimAttachment: (id, index) => api.delete(`/api/insurance/claims/${id}/attachments/${index}`),

  // Claim line items (adjudication)
  getClaimItems: (id) => api.get(`/api/insurance/claims/${id}/items`),
  adjudicateClaimItem: (claimId, itemId, data) => api.put(`/api/insurance/claims/${claimId}/items/${itemId}`, data),

  // Remittances (insurer payment batches)
  getRemittances: (params) => api.get('/api/insurance/remittances', { params }),
  getRemittance: (id) => api.get(`/api/insurance/remittances/${id}`),
  createRemittance: (data) => api.post('/api/insurance/remittances', data),
  updateRemittance: (id, data) => api.put(`/api/insurance/remittances/${id}`, data),
  detachClaimFromRemittance: (remittanceId, claimId) => api.delete(`/api/insurance/remittances/${remittanceId}/claims/${claimId}`),

  // Batch export (CSV download)
  exportClaims: (params) => api.get('/api/insurance/claims-export', { params, responseType: 'blob' }),

  // Stats & Providers
  getStats: () => api.get('/api/insurance/stats'),
  getProviders: () => api.get('/api/insurance/providers'),
  createProvider: (data) => api.post('/api/insurance/providers', data),
  updateProvider: (id, data) => api.put(`/api/insurance/providers/${id}`, data),
  deleteProvider: (id) => api.delete(`/api/insurance/providers/${id}`),
  uploadProviderLogo: (id, formData) => api.post(`/api/insurance/providers/${id}/upload-logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Outpatient Therapy / OPD Care APIs
export const outpatientTherapyAPI = {
  // Plans
  createPlan: (data) => api.post('/api/outpatient-therapy/plans', data),
  getPlans: (params) => api.get('/api/outpatient-therapy/plans', { params }),
  getPlan: (id) => api.get(`/api/outpatient-therapy/plans/${id}`),
  updatePlan: (id, data) => api.put(`/api/outpatient-therapy/plans/${id}`, data),

  // Sessions
  getTodaySessions: () => api.get('/api/outpatient-therapy/sessions/today'),
  getUpcomingSessions: (days = 7) => api.get('/api/outpatient-therapy/sessions/upcoming', { params: { days } }),
  administerSession: (id, data) => api.put(`/api/outpatient-therapy/sessions/${id}/administer`, data),
  missSession: (id, data) => api.put(`/api/outpatient-therapy/sessions/${id}/miss`, data),

  // Vitals
  createVitals: (data) => api.post('/api/outpatient-therapy/vitals', data),
  getVitals: (planId) => api.get(`/api/outpatient-therapy/vitals/${planId}`),

  // Medications
  createMedication: (data) => api.post('/api/outpatient-therapy/medications', data),
  getMedications: (planId) => api.get(`/api/outpatient-therapy/medications/${planId}`),

  // Fluids
  createFluid: (data) => api.post('/api/outpatient-therapy/fluids', data),
  getFluids: (planId, dateFilter) => api.get(`/api/outpatient-therapy/fluids/${planId}`, { params: dateFilter ? { date_filter: dateFilter } : {} }),
  getFluidSummary: (planId, dateFilter) => api.get(`/api/outpatient-therapy/fluids/${planId}/summary`, { params: dateFilter ? { date_filter: dateFilter } : {} }),

  // Notes
  createNote: (data) => api.post('/api/outpatient-therapy/notes', data),
  getNotes: (planId) => api.get(`/api/outpatient-therapy/notes/${planId}`),

  // Dashboard
  getDashboard: () => api.get('/api/outpatient-therapy/dashboard'),

  // OPD Requests (Queue)
  getRequests: (params) => api.get('/api/outpatient-therapy/requests', { params }),
  getRequest: (id) => api.get(`/api/outpatient-therapy/requests/${id}`),
  createRequest: (data) => api.post('/api/outpatient-therapy/requests', data),
  approveRequest: (id, data) => api.put(`/api/outpatient-therapy/requests/${id}/approve`, data),
  rejectRequest: (id, data) => api.put(`/api/outpatient-therapy/requests/${id}/reject`, data),
  deleteRequest: (id) => api.delete(`/api/outpatient-therapy/requests/${id}`),
};

// Chronic Care APIs
export const chronicCareAPI = {
  // Dashboard
  getDashboard: () => api.get('/api/chronic-care/dashboard'),

  // Enrollments
  getEnrollments: (params) => api.get('/api/chronic-care/enrollments', { params }),
  getEnrollment: (id) => api.get(`/api/chronic-care/enrollments/${id}`),
  createEnrollment: (data) => api.post('/api/chronic-care/enrollments', data),
  updateEnrollment: (id, data) => api.put(`/api/chronic-care/enrollments/${id}`, data),
  deleteEnrollment: (id) => api.delete(`/api/chronic-care/enrollments/${id}`),

  // Visits
  getEnrollmentVisits: (enrollmentId) => api.get(`/api/chronic-care/enrollments/${enrollmentId}/visits`),
  getPatientVisits: (patientId) => api.get(`/api/chronic-care/visits/patient/${patientId}`),
  createVisit: (data) => api.post('/api/chronic-care/visits', data),
  updateVisit: (id, data) => api.put(`/api/chronic-care/visits/${id}`, data),
  deleteVisit: (id) => api.delete(`/api/chronic-care/visits/${id}`),

  // Medications
  getEnrollmentMedications: (enrollmentId, activeOnly) => api.get(`/api/chronic-care/enrollments/${enrollmentId}/medications`, { params: activeOnly ? { active_only: true } : {} }),
  createMedication: (data) => api.post('/api/chronic-care/medications', data),
  updateMedication: (id, data) => api.put(`/api/chronic-care/medications/${id}`, data),
  deleteMedication: (id) => api.delete(`/api/chronic-care/medications/${id}`),

  // Goals
  getEnrollmentGoals: (enrollmentId) => api.get(`/api/chronic-care/enrollments/${enrollmentId}/goals`),
  createGoal: (data) => api.post('/api/chronic-care/goals', data),
  updateGoal: (id, data) => api.put(`/api/chronic-care/goals/${id}`, data),
  deleteGoal: (id) => api.delete(`/api/chronic-care/goals/${id}`),

  // Alerts
  getAlerts: (params) => api.get('/api/chronic-care/alerts', { params }),
  createAlert: (data) => api.post('/api/chronic-care/alerts', data),
  updateAlert: (id, data) => api.put(`/api/chronic-care/alerts/${id}`, data),
  acknowledgeAlert: (id) => api.post(`/api/chronic-care/alerts/${id}/acknowledge`),
  resolveAlert: (id) => api.post(`/api/chronic-care/alerts/${id}/resolve`),

  // Patient summary
  getPatientSummary: (patientId) => api.get(`/api/chronic-care/patient/${patientId}/summary`),

  // Enrollment Queue
  getQueue: (params) => api.get('/api/chronic-care/queue', { params }),
  createQueueReferral: (data) => api.post('/api/chronic-care/queue', data),
  reviewQueueItem: (queueId, data) => api.put(`/api/chronic-care/queue/${queueId}/review`, data),
  enrollFromQueue: (queueId, data) => api.post(`/api/chronic-care/queue/${queueId}/enroll`, data),
  deleteQueueItem: (queueId) => api.delete(`/api/chronic-care/queue/${queueId}`),

  // Dispositions
  getDispositions: (params) => api.get('/api/chronic-care/dispositions', { params }),
  createDisposition: (data) => api.post('/api/chronic-care/dispositions', data),
  updateDisposition: (id, data) => api.put(`/api/chronic-care/dispositions/${id}`, data),
  deleteDisposition: (id) => api.delete(`/api/chronic-care/dispositions/${id}`),

  // Programs
  getPrograms: (params) => api.get('/api/chronic-care/programs', { params }),
  getProgram: (id) => api.get(`/api/chronic-care/programs/${id}`),
  createProgram: (data) => api.post('/api/chronic-care/programs', data),
  updateProgram: (id, data) => api.put(`/api/chronic-care/programs/${id}`, data),
  deleteProgram: (id) => api.delete(`/api/chronic-care/programs/${id}`),
  seedDefaultPrograms: () => api.post('/api/chronic-care/programs/seed-defaults'),

  // Refill Requests
  getRefills: (enrollmentId) => api.get(`/api/chronic-care/enrollments/${enrollmentId}/refills`),
  requestRefill: (enrollmentId, data) => api.post(`/api/chronic-care/enrollments/${enrollmentId}/request-refill`, data),
};

export const clinicalParametersAPI = {
  list: (params) => api.get('/api/clinical-parameters', { params }),
  get: (id) => api.get(`/api/clinical-parameters/${id}`),
  create: (data) => api.post('/api/clinical-parameters', data),
  update: (id, data) => api.put(`/api/clinical-parameters/${id}`, data),
  delete: (id) => api.delete(`/api/clinical-parameters/${id}`),
  validate: (data) => api.post('/api/clinical-parameters/validate', data),
  validateBatch: (data) => api.post('/api/clinical-parameters/validate-batch', data),
  compute: (data) => api.post('/api/clinical-parameters/compute', data),
  computeBatch: (data) => api.post('/api/clinical-parameters/compute-batch', data),
};

// Quick Reports APIs (scheduled email reports)
export const quickReportAPI = {
  getSections: () => api.get('/api/quick-reports/sections'),
  searchUsers: (q) => api.get('/api/quick-reports/users/search', { params: { q } }),
  resolveWhatsApp: (emails) => api.post('/api/quick-reports/users/whatsapp', { emails }),
  recipientProfile: (recipient) => api.get('/api/quick-reports/recipient-profile', { params: { recipient } }),
  listSubscriptions: () => api.get('/api/quick-reports/subscriptions'),
  createSubscription: (data) => api.post('/api/quick-reports/subscriptions', data),
  updateSubscription: (id, data) => api.put(`/api/quick-reports/subscriptions/${id}`, data),
  deleteSubscription: (id) => api.delete(`/api/quick-reports/subscriptions/${id}`),
  duplicateSubscription: (id) => api.post(`/api/quick-reports/subscriptions/${id}/duplicate`),
  sendNow: (id) => api.post(`/api/quick-reports/subscriptions/${id}/send`),
  downloadPdf: (id) => api.get(`/api/quick-reports/subscriptions/${id}/pdf`, { responseType: 'blob' }),
  getLogs: (id) => api.get(`/api/quick-reports/subscriptions/${id}/logs`),
  preview: (data) => api.post('/api/quick-reports/preview', data),
};

export default api;
